// DetailedDashboard.jsx  (Node v12 friendly, no optional chaining, no new syntax)
// ✅ Keeps your existing UI + Performance Comparison bar graph + Trend chart + CSS classnames unchanged
// ✅ Adds GR-level API fetch like City-level fetch
// ✅ Passes gr_status keys exactly as you listed (all vs gurgaon)
// ✅ GR filters (Dealer/GR/DeliveryTime) work for BOTH API rows + fallback local rows

import React, { useEffect, useMemo, useRef, useState } from "react"
import { AgGridReact } from "@ag-grid-community/react";

import Select from "react-select";

import "./DetailedDashboard.css"
import { TransitTimeTrendChart } from "./TransitTimeTrendChart.jsx"
import { DashboardHeaderBanner } from "./DashboardHeaderSlot.jsx";
import redirectURL from "../../redirectURL"
import {
  DEFAULT_PERFORMANCE_CONFIG,
  fetchPerformanceConfig,
  performanceLabel,
  performanceFill,
  performanceFillForLabel,
  performanceBadgeClass,
  PerformanceLegend,
} from "./performanceConfig"
import "@ag-grid-community/core/dist/styles/ag-grid.css";
import "@ag-grid-community/core/dist/styles/ag-theme-balham.css";


export default function DetailedDashboard({ globalFilters, activeSubTab = "all" }) {
  const [data, setData] = useState([])
  const [processedData, setProcessedData] = useState([])
  const [filteredProcessedData, setFilteredProcessedData] = useState([])
  const [loading, setLoading] = useState(true)

  // Performance bands from Masters > Hub Performance Config, re-read on every mount
  // so a change there shows on the next dashboard load.
  const [perfConfig, setPerfConfig] = useState(DEFAULT_PERFORMANCE_CONFIG)
  useEffect(function () {
    var cancelled = false
    fetchPerformanceConfig().then(function (config) {
      if (!cancelled) setPerfConfig(config)
    })
    return function () { cancelled = true }
  }, [])

  const [currentView, setCurrentView] = useState("hub") // "hub" | "city" | "gr"
  const [selectedHub, setSelectedHub] = useState("")
  const [selectedCity, setSelectedCity] = useState("")
  const [selectedGRType, setSelectedGRType] = useState("")
  const [showHubOverview, setShowHubOverview] = useState(true)
  const [lastRefreshTime, setLastRefreshTime] = useState(null)

  // ✅ keep selected warehouse for clicked hub row (needed for GR payload fallback)
  const [selectedWarehouseName, setSelectedWarehouseName] = useState("")

  // GR Level Filters
  const [selectedDealerCode, setSelectedDealerCode] = useState("")
  const [selectedGRNumber, setSelectedGRNumber] = useState("")
  const [selectedDeliveryTime, setSelectedDeliveryTime] = useState("")

  const [gurgaonProcessedData, setGurgaonProcessedData] = useState([])
  const [showGurgaonHubOverview, setShowGurgaonHubOverview] = useState(true)
  var [chartView, setChartView] = useState("gr")
  const [gurgaonChartView, setGurgaonChartView] = useState("gr")
  var [fromDate, setFromDate] = useState(function () {
    var today = new Date();
    var d = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    var targetMonth = d.getMonth() - 4;
    d.setMonth(targetMonth);

    if (d.getMonth() !== ((targetMonth % 12) + 12) % 12) {
      d = new Date(today.getFullYear(), today.getMonth() - 3, 0);
    }

    return formatInputDate(d);
  });

  var [toDate, setToDate] = useState(function () {
    return formatInputDate(new Date());
  });
  const [trendWarehouse, setTrendWarehouse] = useState("all")
  const [trendHub, setTrendHub] = useState("all")
  var [timeRangeAll, setTimeRangeAll] = useState("weekly")

  const [countersData, setCountersData] = useState({
    totalHubs: 0,
    totalGRs: 0,
    deliveredGRs: 0,
    within48Hrs: 0,
    gt48Hrs: 0,
    intransitGRs: 0,
    invalidGRs: 0,
    unmappedHubGRs: 0,
    savings: 0,
    within48HrsPct: 0,
    gt48HrsPct: 0
  })
  const [hubSummary, setHubSummary] = useState([])
  const [perfComparison, setPerfComparison] = useState([])
  const [gurgaonCountersData, setGurgaonCountersData] = useState({
    gurgaonHubs: 0,
    totalGRs: 0,
    deliveredGRs: 0,
    within120Hrs: 0,
    gt120Hrs: 0,
    intransitGRs: 0,
    invalidGRs: 0,
    unmappedHubGRs: 0,
    savings: 0,
    within120HrsPct: 0,
    gt120HrsPct: 0
  })
  const [gurgaonHubSummary, setGurgaonHubSummary] = useState([])
  const [gurgaonPerfComparison, setGurgaonPerfComparison] = useState([])

  var [gurgoanTrendHub, setGurgoanTrendHub] = useState("all")
  var [gurgaonWarehouse, setGurgaonWarehouse] = useState("all")
  var [timeRangeGurgaon, setTimeRangeGurgaon] = useState("weekly")

  // ===== Trend Data states =====
  var [trendApiAll, setTrendApiAll] = useState([])
  var [trendApiGurgaon, setTrendApiGurgaon] = useState([])

  // ✅ City-level API data (python-backed endpoint)
  const [cityLevelRows, setCityLevelRows] = useState([])
  const [cityLevelLoading, setCityLevelLoading] = useState(false)

  const [gurgaonCityLevelRows, setGurgaonCityLevelRows] = useState([])
  const [gurgaonCityLevelLoading, setGurgaonCityLevelLoading] = useState(false)

  // ✅ NEW: GR-level API data (python-backed endpoint)
  const [grLevelRows, setGrLevelRows] = useState([])
  const [grLevelLoading, setGrLevelLoading] = useState(false)
  const [selectedWarehouses, setSelectedWarehouses] = useState([{ label: "All", value: "all" }])
  const [warehousesList, setWarehousesList] = useState([])
  const selectedWarehousesRef = useRef(selectedWarehouses)
  const [warehouseHubMap, setWarehouseHubMap] = useState({}); // { MSILG: ["PUNE", ...] }
  const [warehouseOptions, setWarehouseOptions] = useState([{ label: "All", value: "all" }]);
  const fetchSeqRef = useRef(0);
  var [trendLoading, setTrendLoading] = useState(false);
  var warehouseNameMap = {
    MSILG: "GURGAON",
    MSILK: "KHARKHODA"
  };


  useEffect(
    function () {
      setTrendLoading(true);

      if (activeSubTab === "all") {
        setTimeRangeAll("weekly");
        setTrendHub("all");
        setTrendWarehouse("all");
        setTrendApiAll([]);
      } else {
        setTimeRangeGurgaon("weekly");
        setGurgoanTrendHub("all");
        setGurgaonWarehouse("all");
        setTrendApiGurgaon([]);
      }
    },
    [activeSubTab]
  );


  useEffect(function () {
    var cancelled = false;

    async function init() {
      // ✅ invalidate any previous in-flight fetchData immediately
      fetchSeqRef.current += 1;

      // ✅ reset UI so old tab counters don't show
      setLoading(true);
      setHubSummary([]);
      setPerfComparison([]);
      setGurgaonHubSummary([]);
      setGurgaonPerfComparison([]);

      setCountersData({
        totalHubs: 0, totalGRs: 0, deliveredGRs: 0, within48Hrs: 0, gt48Hrs: 0, intransitGRs: 0, invalidGRs: 0, unmappedHubGRs: 0, savings: 0, within48HrsPct: 0, gt48HrsPct: 0
      });
      setGurgaonCountersData({
        gurgaonHubs: 0, totalGRs: 0, deliveredGRs: 0, within120Hrs: 0, gt120Hrs: 0, intransitGRs: 0, invalidGRs: 0, unmappedHubGRs: 0, savings: 0, within120HrsPct: 0, gt120HrsPct: 0
      });

      const defaultWh = [{ label: "All", value: "all" }];
      setSelectedWarehouses(defaultWh);

      setCurrentView("hub");
      setSelectedHub("");
      setSelectedCity("");
      setSelectedGRType("");
      setCityLevelRows([]);
      setGurgaonCityLevelRows([]);
      setGrLevelRows([]);

      try {
        var apiRows = await fetchWarehousesAndHubsFromApi(activeSubTab);
        var warehouses = [];
        if (!cancelled) {
          var map = buildWarehouseHubMap(apiRows);
          setWarehouseHubMap(map);
          setWarehouseOptions(buildWarehouseOptionsFromMap(map, activeSubTab));
          setWarehousesList(buildWarehouseOptionsFromMap(map, activeSubTab));
          warehouses = buildWarehouseOptionsFromMap(map, activeSubTab)
        }
      } catch (e) {
        console.error("fetchWarehousesAndHubsFromApi error", e);
        if (!cancelled) {
          setWarehouseHubMap({});
          setWarehouseOptions([{ label: "All", value: "all" }]);
          setWarehousesList([{ label: "All", value: "all" }]);
          warehouses = []

        }
      }

      await fetchData(defaultWh, warehouses); // uses request-id guard now
    }

    init();
    return function () { cancelled = true; };
  }, [activeSubTab]);

  useEffect(() => {
    selectedWarehousesRef.current = selectedWarehouses
  }, [selectedWarehouses])
  useEffect(function () {
    // ---- HUB RESET (ALL TAB) ----
    var allowedAllHubs = getTrendHubOptionsForAllTab();
    if (trendHub !== "all" && allowedAllHubs.indexOf(trendHub) === -1) {
      setTrendHub("all");
    }

    // ---- HUB RESET (GURGAON TAB) ----
    var allowedGurgaonHubs = getTrendHubOptionsForAllTab();
    // If Gurgaon hubs differ, use separate function here
    if (gurgoanTrendHub !== "all" && allowedGurgaonHubs.indexOf(gurgoanTrendHub) === -1) {
      setGurgoanTrendHub("all");
    }

    // ---- WAREHOUSE RESET (ALL TAB) ----
    if (
      trendWarehouse !== "all" &&
      !warehouseHubMap.hasOwnProperty(trendWarehouse)
    ) {
      setTrendWarehouse("all");
    }

    // ---- WAREHOUSE RESET (GURGAON TAB) ----
    if (
      gurgaonWarehouse !== "all" &&
      !warehouseHubMap.hasOwnProperty(gurgaonWarehouse)
    ) {
      setGurgaonWarehouse("all");
    }

  }, [selectedWarehouses, warehouseHubMap]);



  useEffect(function () {
    var cancelled = false

    async function run() {
      try {
        var isAllTab = activeSubTab === "all"
        var type = isAllTab ? "all" : "gurgaon"
        var timeRange = isAllTab ? timeRangeAll : timeRangeGurgaon
        var hub = isAllTab ? trendHub : gurgoanTrendHub
        var warehouse = isAllTab ? trendWarehouse : gurgaonWarehouse

        var tdata = await fetchTrendData(
          type,
          timeRange,
          hub,
          warehouse,
          selectedWarehousesRef.current // ✅ always latest
        )

        if (!cancelled) {
          if (isAllTab) setTrendApiAll(tdata)
          else setTrendApiGurgaon(tdata)
        }
      } catch (e) {
        console.error("Trend API error", e)
        if (!cancelled) {
          if (activeSubTab === "all") setTrendApiAll([])
          else setTrendApiGurgaon([])
        }
      }
    }

    run()
    return function () { cancelled = true }
  }, [
    activeSubTab,
    timeRangeAll, trendHub, trendWarehouse,
    timeRangeGurgaon, gurgoanTrendHub, gurgaonWarehouse, fromDate,
    toDate
    // ✅ remove selectedWarehouses from deps if you only want submit to trigger it
  ])

  async function fetchWarehousesAndHubsFromApi(activeSubTab) {
    var url = "/msildashboard/fetchWarehousesAndHubs"

    var payload = { tab: getMasterTab(activeSubTab) } // ✅ tab depends on switch
    var response = await redirectURL.post(url, payload)

    // backend returns: { status:"Success", data:[...] }
    var data = response && response.data ? response.data.data : null

    if (!Array.isArray(data)) {
      // eslint-disable-next-line no-console
      console.warn(
        "Warehouses/Hubs API failed or unexpected:",
        response && response.data ? response.data : response
      )
      return []
    }

    return data
  }

  async function fetchData(overrideWarehouses, warehouses) {
    const seq = ++fetchSeqRef.current;  // ✅ mark this call as latest
    setLoading(true);

    try {
      const params = { from_date: fromDate, to_date: toDate };

      const wh = overrideWarehouses || selectedWarehouses || [{ label: "All", value: "all" }];
      const warehouseValues = wh.map(item => item.value);

      const warehouseList = warehouses || warehousesList;
      const allWarehouseValues = warehouseList
        .filter(item => item.value !== "all")
        .map(item => item.value);
      params.warehouse = warehouseValues.includes("all") ? allWarehouseValues : warehouseValues;

      const url =
        activeSubTab === "all"
          ? "/msildashboard/fetchDetailedDashboardAllHub"
          : "/msildashboard/fetchDetailedDashboardGurgaonHub";

      const response = await redirectURL.post(url, params);

      // ✅ if user switched tabs while request was running, ignore
      if (seq !== fetchSeqRef.current) return;

      const responseData =
        response && response.data && response.data.status === "Success"
          ? response.data
          : null;

      if (!responseData) return;

      const countersResp = responseData.live_layer_1_ui_data || {};
      const hubSummaryResp = Array.isArray(responseData.live_layer_2_ui_data) ? responseData.live_layer_2_ui_data : [];
      const perfCompResp = Array.isArray(responseData.live_layer_3_ui_data) ? responseData.live_layer_3_ui_data : [];

      if (activeSubTab === "all") {
        setCountersData({
          totalHubs: countersResp.total_hubs || 0,
          totalGRs: countersResp.total_grs || 0,
          deliveredGRs: countersResp.delivered_gr_count || 0,
          within48Hrs: countersResp.delivered_gr_within_48h || 0,
          gt48Hrs: countersResp.delivered_gr_above_48h || 0,
          intransitGRs: countersResp.intransit_grs || 0,
          invalidGRs: countersResp.invalid_grs || 0,
          unmappedHubGRs: countersResp.unmapped_hub_grs_count || 0,
          within48HrsPct: countersResp.delivered_within_48h_percentage || 0,
          gt48HrsPct: countersResp.delivered_above_48h_percentage || 0,
          savings: convertToMillion(countersResp.savings)
        });
        setHubSummary(hubSummaryResp);
        setPerfComparison(perfCompResp);
      } else {
        setGurgaonCountersData({
          gurgaonHubs: countersResp.total_hubs || 0,
          totalGRs: countersResp.total_grs || 0,
          deliveredGRs: countersResp.delivered_gr_count || 0,
          within120Hrs: countersResp.delivered_gr_within_120h || 0,
          gt120Hrs: countersResp.delivered_gr_above_120h || 0,
          intransitGRs: countersResp.intransit_grs || 0,
          invalidGRs: countersResp.invalid_grs || 0,
          unmappedHubGRs: countersResp.unmapped_hub_grs_count || 0,
          within120HrsPct: countersResp.delivered_within_120h_percentage || 0,
          gt120HrsPct: countersResp.delivered_above_120h_percentage || 0,
          savings: convertToMillion(countersResp.savings)
        });
        setGurgaonHubSummary(hubSummaryResp);
        setGurgaonPerfComparison(perfCompResp);
      }

      setLastRefreshTime(new Date());
    } catch (err) {
      // ✅ ignore stale errors too
      if (seq !== fetchSeqRef.current) return;
      console.error("Error in fetchData:", err);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }
  async function fetchTrendData(type, timeRange, hub, warehouse, overrideWarehouses) {
    const url =
      type === "all"
        ? "/msildashboard/fetchDetailedTransitTimeTrendAll"
        : "/msildashboard/fetchDetailedTransitTimeTrendGurgaon"

    const params = { by: timeRange, hub: hub, from_date: fromDate, to_date: toDate }
    setTrendLoading(true);


    const wh = overrideWarehouses || selectedWarehouses || [{ label: "All", value: "all" }]

    const warehouseValues = wh.map((item) => item.value)

    if (warehouseValues.length && !warehouseValues.includes("all")) {
      params.warehouse = warehouseValues
    } else {
      params.warehouse = [warehouse || "all"]
    }

    try {
      const response = await redirectURL.post(url, params)

      if (response && response.data && response.data.status === "Success") {
        return Array.isArray(response.data.trend_data) ? response.data.trend_data : []
      }

      console.warn("Trend API failed:", response && response.data ? response.data : response)
      return []
    } catch (err) {
      console.error("fetchTrendData error:", err)
      return []
    } finally {
      setTrendLoading(false);
    }
  }
  function moneyCell(colorClass) {
    return function (params) {
      var value = Number(params && params.value);
      if (!isFinite(value)) value = 0;

      // Convert to Million
      var millionValue = value / 1000000;

      // Format to 2 decimals and add M
      var formattedValue = millionValue.toFixed(2) + "M";

      return (
        '<div class="tc">' +
        '<div class="bigNumber ' + colorClass + '">' + formattedValue + '</div>' +
        '</div>'
      );
    };
  }


  function savingsCell() {
    return function (params) {
      var value = Number(params && params.value);
      if (!isFinite(value)) value = 0;

      // Convert rupees to million
      var millionValue = value / 1000000;

      var formattedValue = millionValue.toFixed(2) + "M";

      return (
        '<div class="tc">' +
        '<div class="bigNumber greenText">' + formattedValue + '</div>' +
        '</div>'
      );
    };
  }

  function convertToMillion(value) {
    const numberValue = Number(value) || 0;   // convert to number safely
    const millionValue = numberValue / 1000000;
    return Number(millionValue.toFixed(2));   // keep as number (better for grid sorting)
  }


  // ✅ city/gr date payload wants ISO start of day
  function toIsoStartOfDay(dateStr) {
    if (!dateStr) return ""
    return String(dateStr) + "T00:00:00"
  }

  // ✅ map your UI click types -> exact backend gr_status keys you listed
  function mapGrTypeToStatusKey(grType, tab) {
    if (tab === "all") {
      if (grType === "total") return "total_grs"
      if (grType === "delivered") return "delivered_grs"
      if (grType === "transit") return "transit_grs"
      if (grType === "within24hr") return "grs_within_24h"
      // you can add a UI hook later for this:
      if (grType === "within24_48hr") return "grs_within_24_48h"
      if (grType === "within48hr") return "grs_within_48h"
      if (grType === "over48hr") return "grs_above_48h"
      if (grType === "invalid") return "invalid_grs"
      return "delivered_grs"
    }

    // gurgaon
    if (grType === "total") return "total_grs"
    if (grType === "delivered") return "delivered_grs"
    if (grType === "transit") return "transit_grs"
    if (grType === "within90hr") return "grs_within_90h"
    // you can add a UI hook later for this:
    if (grType === "within90_120hr") return "grs_within_90_120h"
    if (grType === "within120hr") return "grs_within_120h"
    if (grType === "over120hr") return "grs_above_120h"
    if (grType === "invalid") return "invalid_grs"
    return "delivered_grs"
  }

  // ✅ City level endpoint fetch
  async function fetchCityLevelDataForHub(payload) {
    // IMPORTANT: do not change UI logic; only fix response parsing + state assignment
    if (activeSubTab === "all") setCityLevelLoading(true);
    else setGurgaonCityLevelLoading(true);

    try {
      const url = "/msildashboard/fetchAllWarehousesCityLevelData"
      const response = await redirectURL.post(url, payload)

      var out = []
      // ✅ use response.data.trend (your python endpoint) + safe fallbacks
      if (response && response.data && Array.isArray(response.data.trend)) {
        out = response.data.trend
      } else if (response && response.data && Array.isArray(response.data.trend_data)) {
        out = response.data.trend_data
      } else if (response && response.data && Array.isArray(response.data.data) && response.data.data.length) {
        out = response.data.data
      } else if (Array.isArray(response && response.data)) {
        out = response.data
      } else {
        out = []
      }

      if (activeSubTab === "all") {
        setCityLevelRows(out)
      } else {
        setGurgaonCityLevelRows(out)
      }
      return out
    } catch (e) {
      console.error("fetchCityLevelDataForHub error:", e)
      setCityLevelRows([])
      setGurgaonCityLevelRows([])
      return []
    } finally {
      if (activeSubTab === "all") setCityLevelLoading(false);
      else setGurgaonCityLevelLoading(false);
    }
  }

  // ✅ NEW: GR level endpoint fetch (same style as city)
  async function fetchGrLevelData(payload) {
    setGrLevelLoading(true)
    try {
      const url = "/msildashboard/fetchAllWarehousesGRLevelData"
      const response = await redirectURL.post(url, payload)

      var out = []
      if (response && response.data && response.data.status === "Success") {
        out = Array.isArray(response.data.trend_data) ? response.data.trend_data : []
      } else if (response && response.data && Array.isArray(response.data.trend)) {
        out = response.data.trend
      } else if (response && response.data && Array.isArray(response.data.data)) {
        out = response.data.data
      } else if (Array.isArray(response && response.data)) {
        out = response.data
      } else {
        out = []
      }

      setGrLevelRows(out)
      return out
    } catch (e) {
      console.error("fetchGrLevelData error:", e)
      setGrLevelRows([])
      return []
    } finally {
      setGrLevelLoading(false)
    }
  }

  function parseDateTime(dateStr) {
    if (!dateStr || String(dateStr).trim() === "") return null
    try {
      const parts = String(dateStr).split(" ")
      if (parts.length !== 2) return null
      const datePart = parts[0]
      const timePart = parts[1]

      const dParts = datePart.split("-")
      if (dParts.length !== 3) return null
      const day = dParts[0]
      const month = dParts[1]
      const year = dParts[2]

      const tParts = timePart.split(":")
      if (tParts.length !== 2) return null
      const hour = tParts[0]
      const minute = tParts[1]

      const monthMap = {
        Jan: 0,
        Feb: 1,
        Mar: 2,
        Apr: 3,
        May: 4,
        Jun: 5,
        Jul: 6,
        Aug: 7,
        Sep: 8,
        Oct: 9,
        Nov: 10,
        Dec: 11,
      }

      return new Date(parseInt(year, 10), monthMap[month], parseInt(day, 10), parseInt(hour, 10), parseInt(minute, 10))
    } catch (e) {
      return null
    }
  }

  function formatRefreshTime(date) {
    if (!date) return "Never"
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  }

  function getFilteredData() {
    let filtered = data

    if (globalFilters && globalFilters.selectedWarehouse && globalFilters.selectedWarehouse !== "all") {
      filtered = filtered.filter((row) => row["Consigner Code"] === globalFilters.selectedWarehouse)
    }

    return filtered
  }

  function getCityDetails(hub) {
    const filteredData = getFilteredData().filter((row) => row["Consigner Code"] === hub)
    const cityGroups = filteredData.reduce((acc, row) => {
      const city = row.City || row["Consignee Code"] || "Unknown"
      if (!acc[city]) acc[city] = []
      acc[city].push(row)
      return acc
    }, {})

    return Object.keys(cityGroups).map((city) => {
      const rows = cityGroups[city]
      const deliveredGRs = rows.filter((r) => r.Status === "Delivered").length
      const totalGRs = rows.length
      const transitGRs = totalGRs - deliveredGRs

      const grsWithin48Hr = rows.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 35) <= 48).length
      const grsWithin24Hr = rows.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 35) <= 24).length
      const grsOver48Hr = rows.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 35) > 48).length

      return {
        city: city,
        totalGRs: totalGRs,
        deliveredGRs: deliveredGRs,
        transitGRs: transitGRs,
        grsWithin48Hr: grsWithin48Hr,
        grsWithin24Hr: grsWithin24Hr,
        grsOver48Hr: grsOver48Hr,
        percent48Hr: totalGRs ? (grsWithin48Hr / totalGRs) * 100 : 0,
        percent24Hr: totalGRs ? (grsWithin24Hr / totalGRs) * 100 : 0,
        percentOver48Hr: totalGRs ? (grsOver48Hr / totalGRs) * 100 : 0,
        firstMileTime: parseFloat(rows[0] && rows[0]["Transit time-1st Mile(Hrs.Min)"]) || 3.5,
        lastMileTime: parseFloat(rows[0] && rows[0]["Transit time-Last Mile(Hrs.Min)"]) || 2.0,
      }
    })
  }

  function getGurgaonCityDetails(hub) {
    const filteredData = getFilteredData().filter((row) => row["Consigner Code"] === hub)
    const cityGroups = filteredData.reduce((acc, row) => {
      const city = row.City || row["Consignee Code"] || "Unknown"
      if (!acc[city]) acc[city] = []
      acc[city].push(row)
      return acc
    }, {})

    return Object.keys(cityGroups).map((city) => {
      const rows = cityGroups[city]
      const deliveredGRs = rows.filter((r) => r.Status === "Delivered").length
      const totalGRs = rows.length
      const transitGRs = totalGRs - deliveredGRs

      const grsWithin120Hr = rows.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 85) <= 120).length
      const grsWithin90Hr = rows.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 85) <= 90).length
      const grsOver120Hr = rows.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 85) > 120).length

      return {
        city: city,
        totalGRs: totalGRs,
        deliveredGRs: deliveredGRs,
        transitGRs: transitGRs,
        grsWithin48Hr: grsWithin120Hr,
        grsWithin24Hr: grsWithin90Hr,
        grsOver48Hr: grsOver120Hr,
        percent48Hr: totalGRs ? (grsWithin120Hr / totalGRs) * 100 : 0,
        percent24Hr: totalGRs ? (grsWithin90Hr / totalGRs) * 100 : 0,
        percentOver48Hr: totalGRs ? (grsOver120Hr / totalGRs) * 100 : 0,
        firstMileTime: parseFloat(rows[0] && rows[0]["Transit time-1st Mile(Hrs.Min)"]) || 18,
        lastMileTime: parseFloat(rows[0] && rows[0]["Transit time-Last Mile(Hrs.Min)"]) || 6,
      }
    })
  }

  function getGRDetails(hub, city, type) {
    let filteredData = getFilteredData()
      .filter((row) => row["Consigner Code"] === hub)
      .filter((row) => (row.City || row["Consignee Code"]) === city)

    if (type === "delivered") filteredData = filteredData.filter((r) => r.Status === "Delivered")
    if (type === "transit") filteredData = filteredData.filter((r) => r.Status !== "Delivered")

    if (type === "within48hr") filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 35) <= 48)
    if (type === "within24hr") filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 35) <= 24)
    if (type === "over48hr") filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 35) > 48)

    if (type === "within90hr") filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 85) <= 90)
    if (type === "within120hr") filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 85) <= 120)
    if (type === "over120hr") filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 85) > 120)

    // filters
    if (selectedDealerCode && selectedDealerCode !== "all") {
      filteredData = filteredData.filter((r) => r["Consignee Code"] === selectedDealerCode)
    }
    if (selectedGRNumber && selectedGRNumber !== "all") {
      filteredData = filteredData.filter((r) => r["Consignment Code"] === selectedGRNumber)
    }
    if (selectedDeliveryTime && selectedDeliveryTime !== "all") {
      if (activeSubTab === "all") {
        if (selectedDeliveryTime === "within48") {
          filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 35) <= 48)
        }
        if (selectedDeliveryTime === "over48") {
          filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 35) > 48)
        }
      } else {
        // gurgaon: keep UI text same, but apply 120h logic
        if (selectedDeliveryTime === "within48") {
          filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 85) <= 120)
        }
        if (selectedDeliveryTime === "over48") {
          filteredData = filteredData.filter((r) => (parseFloat(r["Overall transit time(Hrs.Min)"]) || 85) > 120)
        }
      }
    }

    return filteredData.map((row) => ({
      grNo: row["Consignment Code"],
      consignment: row["Consignment Code"],
      dealerCode: row["Consignee Code"],
      dealerName: row["Consignee Name"],
      wrhs: row["Consigner Code"],
      hub: row["Consigner Code"],
      city: row.City || row["Consignee Code"],
      gateOutDateTime: row["Gate out Time"] || "01-Jan-2025 03:39",
      deliveryDateTime: row["Delivery time at Consignee*"] || "02-Jan-2025 15:40",
      deliveryDuration: parseFloat(row["Overall transit time(Hrs.Min)"]) || 35,
      firstMile: parseFloat(row["Transit time-1st Mile(Hrs.Min)"]) || 3.5,
      lastMile: parseFloat(row["Transit time-Last Mile(Hrs.Min)"]) || 2.0,
    }))
  }

  // ✅ City rows adapter (python endpoint -> UI table model)
  function getCityDetailsFromApiRows() {
    var rows = Array.isArray(cityLevelRows) ? cityLevelRows : []
    return rows.map(function (r) {
      var total = r.total_grs || 0
      var delivered = r.delivered_grs || 0
      var transit = r.transit_grs || 0

      // accept both possible python keys (you had different versions)
      var within24 = r.grs_within_24h || r.delivered_within_24h || 0
      var within24_48 = r.grs_within_24_48h || r.delivered_within_24_48h || 0
      var within48 = r.grs_within_48h || r.delivered_within_48h || 0
      var over48 = r.grs_above_48h || r.delivered_above_48h || 0
      var invalid_grs = r.invalid_grs || r.invalid_grs || 0

      var pct24 = delivered ? (within24 / delivered) * 100 : 0
      var pct48 = delivered ? (within48 / delivered) * 100 : 0
      var pctOver48 = delivered ? (over48 / delivered) * 100 : 0
      var pctWithin24_48 = delivered ? (within24_48 / delivered) * 100 : 0

      return {
        city: r.hub_city || r.City || "Unknown",
        totalGRs: total,
        deliveredGRs: delivered,
        transitGRs: transit,
        grsWithin24Hr: within24,
        grsWithin48Hr: within48,
        grsWithin24_48Hr: within24_48,
        grsOver48Hr: over48,
        percent24Hr: pct24,
        percent48Hr: pct48,
        percentOver48Hr: pctOver48,
        percentWithin24_48Hr: pctWithin24_48,
        firstMileTime:
          typeof r.first_mile_avg_transit_time === "number"
            ? r.first_mile_avg_transit_time
            : parseFloat(r.first_mile_avg_transit_time || 0),
        lastMileTime:
          typeof r.last_mile_avg_transit_time === "number"
            ? r.last_mile_avg_transit_time
            : parseFloat(r.last_mile_avg_transit_time || 0),
        hub_location: r.hub_location || "",
        warehouse: r.warehouse || "",
        hub_operator_code: r.hub_operator_code || "",
        hub_operator_name: r.hub_operator_name || "",
        truck_cost: Number(r.truck_cost || 0).toFixed(2),
        courier_cost: Number(r.courier_cost || 0).toFixed(2),
        savings: Number(r.savings || 0).toFixed(2),
        invalid_grs: invalid_grs
      }
    })
  }

  function getGurgaonCityDetailsFromApiRows() {
    var rows = Array.isArray(gurgaonCityLevelRows) ? gurgaonCityLevelRows : []
    return rows.map(function (r) {
      var total = r.total_grs || 0
      var delivered = r.delivered_grs || 0
      var transit = r.transit_grs || 0

      var within90 = r.grs_within_90h || r.delivered_within_90h || 0
      var within90_120 = r.grs_within_90_120h || r.delivered_within_90_120h || 0
      var within120 = r.grs_within_120h || r.delivered_within_120h || 0
      var over120 = r.grs_above_120h || r.delivered_above_120h || 0
      var invalid_grs = r.invalid_grs || r.invalid_grs || 0

      var pct90 = delivered ? (within90 / delivered) * 100 : 0
      var pct120 = delivered ? (within120 / delivered) * 100 : 0
      var pctOver120 = delivered ? (over120 / delivered) * 100 : 0
      var pctWithin90_120 = delivered ? (within90_120 / delivered) * 100 : 0

      return {
        city: r.hub_city || r.City || "Unknown",
        totalGRs: total,
        deliveredGRs: delivered,
        transitGRs: transit,
        grsWithin90Hr: within90,
        grsWithin120Hr: within120,
        grsOver120Hr: over120,
        grsWithin90_120Hr: within90_120,
        percent90Hr: pct90,
        percent120Hr: pct120,
        percentOver120Hr: pctOver120,
        percentWithin90_120Hr: pctWithin90_120,
        firstMileTime:
          typeof r.first_mile_avg_transit_time === "number"
            ? r.first_mile_avg_transit_time
            : parseFloat(r.first_mile_avg_transit_time || 0),
        lastMileTime:
          typeof r.last_mile_avg_transit_time === "number"
            ? r.last_mile_avg_transit_time
            : parseFloat(r.last_mile_avg_transit_time || 0),
        hub_location: r.hub_location || "",
        warehouse: r.warehouse || "",
        hub_operator_code: r.hub_operator_code || "",
        hub_operator_name: r.hub_operator_name || "",
        truck_cost: Number(r.truck_cost || 0).toFixed(2),
        courier_cost: Number(r.courier_cost || 0).toFixed(2),
        savings: Number(r.savings || 0).toFixed(2),
        invalid_grs: invalid_grs
      }
    })
  }

  // ✅ GR rows adapter (python endpoint -> UI table model)
  function mapApiGrRowToUi(r) {
    // Keep it defensive (python might send different keys)
    return {
      grNo: r.gr_no || r.grNo || r.consignment_code || r["Consignment Code"] || "",
      dealerCode: r.dealer_code || r.dealerCode || r.consignee_code || "",
      dealerName: r.consignee_name || r.dealerName || "",
      wrhs: r.warehouse || r.wrhs || r.consigner_code || selectedWarehouseName || "",
      hub: r.hub || r.hub_location || selectedHub,
      hubOperatorCode: r.hub_operator_code || "",
      hubOperatorName: r.hub_operator_name || "",
      city: r.hub_city || r.city || r.City || selectedCity,
      gateOutDateTime: formatDateTime(
        r.gate_out_time || r.gateOutDateTime || r["Gate out Time"] || ""
      ),
      hubReachedDateTime: formatDateTime(
        r.actual_arrival_at_hub || ""
      ),
      hubLeftDateTime: formatDateTime(
        r.dispatch_time_from_hub_from_lastmile || ""
      ),
      deliveryDateTime: formatDateTime(
        r.delivery_time || r.delivery_time_from_lastmile || r["Delivery time at Consignee*"] || ""
      ),
      firstMile: typeof r.first_mile_transit_time === "number" ? r.first_mile_transit_time : parseFloat(r.first_mile_transit_time || r.first_mile_transit_time || 0),
      lastMile: typeof r.last_mile_transit_time === "number" ? r.last_mile_transit_time : parseFloat(r.last_mile_transit_time || r.last_mile_transit_time || 0),
      deliveryDuration: typeof r.total_transit_time === "number" ? r.total_transit_time : parseFloat(r.total_transit_time || r.total_transit_time || 0),
      truckCost: (r.truck_cost || 0),
      courierCost: (r.courier_cost || 0),
      savings: (r.savings || 0)
    }
  }

  // ✅ base GR rows (API if loaded, else fallback local function)
  function getBaseGrRowsForUi() {
    if (Array.isArray(grLevelRows) && grLevelRows.length) {
      return grLevelRows.map(mapApiGrRowToUi)
    }
    return getGRDetails(selectedHub, selectedCity, selectedGRType)
  }
  // ✅ Extract a "date key" from deliveryDateTime
  // Supports: "02-Jan-2025 15:40", ISO strings, etc.
  // Returns: "02-Jan-2025" (or "" if invalid)
  function getDeliveryDateKey(value) {
    if (!value) return ""

    var s = String(value).trim()
    if (!s) return ""

    // Case 1: "02-Jan-2025 15:40" => take left side
    if (s.indexOf(" ") !== -1 && s.indexOf("-") !== -1) {
      return s.split(" ")[0] // "02-Jan-2025"
    }

    // Case 2: ISO "2025-01-02T15:40:00" or "2025-01-02"
    // convert "YYYY-MM-DD" into "DD-MMM-YYYY" (optional)
    if (s.length >= 10 && s[4] === "-" && s[7] === "-") {
      var yyyy = s.slice(0, 4)
      var mm = s.slice(5, 7)
      var dd = s.slice(8, 10)

      var monthMap = {
        "01": "Jan",
        "02": "Feb",
        "03": "Mar",
        "04": "Apr",
        "05": "May",
        "06": "Jun",
        "07": "Jul",
        "08": "Aug",
        "09": "Sep",
        "10": "Oct",
        "11": "Nov",
        "12": "Dec",
      }

      var mmm = monthMap[mm] || mm
      return dd + "-" + mmm + "-" + yyyy
    }

    return s
  }

  // ✅ sort date keys like "02-Jan-2025"
  function sortDeliveryDateKeys(list) {
    var monthMap = {
      Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
      Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
    }

    function toSortable(d) {
      // expects "DD-MMM-YYYY"
      if (!d) return 0
      var parts = String(d).split("-")
      if (parts.length !== 3) return 0
      var dd = parseInt(parts[0], 10)
      var mmm = parts[1]
      var yyyy = parseInt(parts[2], 10)
      var mm = monthMap[mmm]
      if (isNaN(dd) || typeof mm !== "number" || isNaN(yyyy)) return 0
      return new Date(yyyy, mm, dd).getTime()
    }

    return list.slice().sort(function (a, b) {
      return toSortable(a) - toSortable(b)
    })
  }


  // ✅ Apply GR filters to whichever base rows we are using
  function applyGrFilters(rows) {
    var out = rows.slice(0)

    if (selectedDealerCode && selectedDealerCode !== "all") {
      out = out.filter(function (r) {
        return r.dealerCode === selectedDealerCode
      })
    }

    if (selectedGRNumber && selectedGRNumber !== "all") {
      out = out.filter(function (r) {
        return r.grNo === selectedGRNumber
      })
    }

    // ✅ Delivery Date filter (instead of within48/over48)
    if (selectedDeliveryTime && selectedDeliveryTime !== "all") {
      out = out.filter(function (r) {
        var key = getDeliveryDateKey(r && r.deliveryDateTime ? r.deliveryDateTime : "")
        return key === selectedDeliveryTime
      })
    }


    return out
  }

  function getGrRowsForUi() {
    return applyGrFilters(getBaseGrRowsForUi())
  }

  function getAvailableDealerCodes() {
    var base = getBaseGrRowsForUi()
    var map = {}
    for (var i = 0; i < base.length; i++) {
      var code = base[i] && base[i].dealerCode ? base[i].dealerCode : ""
      if (code) map[code] = true
    }
    return Object.keys(map).sort()
  }

  function getAvailableGRNumbers() {
    var base = getBaseGrRowsForUi()
    var map = {}
    for (var i = 0; i < base.length; i++) {
      var no = base[i] && base[i].grNo ? base[i].grNo : ""
      if (no) map[no] = true
    }
    return Object.keys(map).sort()
  }
  function getAvailableDeliveryDates() {
    var base = getBaseGrRowsForUi()
    var map = {}

    for (var i = 0; i < base.length; i++) {
      var dt = base[i] && base[i].deliveryDateTime ? base[i].deliveryDateTime : ""
      var key = getDeliveryDateKey(dt)
      if (key) map[key] = true
    }

    return sortDeliveryDateKeys(Object.keys(map))
  }


  function handleBack() {
    if (currentView === "gr") {
      setCurrentView("city")
      setSelectedDealerCode("")
      setSelectedGRNumber("")
      setSelectedDeliveryTime("")
      setGrLevelRows([]) // ✅ clear GR api rows when going back
    } else if (currentView === "city") {
      setCurrentView("hub")
      if (activeSubTab === "all") setCityLevelRows([])
      else setGurgaonCityLevelRows([])
      setSelectedWarehouseName("")
      setSelectedCity("")
      setSelectedGRType("")
      setGrLevelRows([])
    }
  }

  function perfBadgeClass(pct) {
    return performanceBadgeClass(pct, perfConfig)
  }

  function perfLabel(pct) {
    return performanceLabel(pct, perfConfig)
  }

  function ButtonSimple(props) {
    var cls = "btn " + (props.variant === "primary" ? "btnPrimary" : "btnOutline")
    return (
      <button className={cls} type="button" onClick={props.onClick}>
        {props.children}
      </button>
    )
  }

  function SelectSimple(props) {
    return (
      <select className="select" value={props.value} onChange={props.onChange} disabled={props.disabled}>
        {props.children}
      </select>
    )
  }

  function regionLabel(hub) {
    if (String(hub).indexOf("DEL") !== -1) return "North Region"
    if (String(hub).indexOf("MUM") !== -1) return "West Region"
    if (String(hub).indexOf("CHE") !== -1) return "South Region"
    if (String(hub).indexOf("KOL") !== -1) return "East Region"
    if (String(hub).indexOf("GUJ") !== -1) return "North Region"
    return "Central Region"
  }
  // -------------------------------
  // AG Grid helpers (same as LiveDashboard style)
  // -------------------------------
  function n(v) {
    var x = typeof v === "number" ? v : parseFloat(v || 0);
    return isNaN(x) ? 0 : x;
  }

  function pctBucket(pct) {
    var p = typeof pct === "number" ? pct : parseFloat(pct || 0);
    if (isNaN(p)) p = 0;
    if (p < 0) p = 0;
    if (p > 100) p = 100;

    var start = Math.floor(p / 10) * 10;
    var end = start + 10;

    if (start === 100) return "100%";
    return start + "% - " + end + "%";
  }

  var setFilterParams = {
    buttons: ["reset", "apply"],
    closeOnApply: true,
    suppressMiniFilter: false,
    refreshValuesOnOpen: true,
  };

  var pctSetFilterParams = {
    buttons: ["reset", "apply"],
    closeOnApply: true,
    suppressMiniFilter: false,
    refreshValuesOnOpen: true,
    comparator: function (a, b) {
      function startNum(x) {
        if (!x) return 0;
        if (x === "100%") return 100;
        var nn = parseInt(String(x).split("%")[0], 10);
        return isNaN(nn) ? 0 : nn;
      }
      return startNum(a) - startNum(b);
    },
  };

  // Gurgaon Hub Overview AG-Grid ColumnDefs (same UI logic, but 120h/90h)
  function getGurgaonHubColumnDefs() {
    return [
      // Warehouse badge
      {
        headerName: "Warehouse",
        field: "warehouse",
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        width: 140,
        cellRenderer: function (params) {
          var value = params && params.value ? params.value : "";
          return (
            '<div class="badgeBox orangeSoft">' +
            '<span class="badgeText orangeText">' + value + "</span>" +
            "</div>"
          );
        },
      },
      {
        headerName: "Warehouse Name",
        field: "warehouse_name",
        valueGetter: function (params) {
          var warehouse = params && params.data && params.data.warehouse ? params.data.warehouse : "";
          return warehouseNameMap[warehouse] || ""
        },
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        width: 140,
        cellRenderer: function (params) {
          var value = params && params.value ? params.value : "";
          return '<div class="hubText">' + value + "</div>";
        },
      },

      // Hub clickable (KEEP YOUR EXISTING CLICK LOGIC)
      {
        headerName: "Hub",
        field: "hub_location",
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        width: 160,
        cellRendererFramework: function (params) {
          var hub = params && params.data ? params.data : null;
          var hubName = hub && hub.hub_location ? hub.hub_location : "";

          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                // ✅ keep existing exact behavior
                setSelectedHub(hubName);
                setSelectedWarehouseName((hub && hub.warehouse) || "");
                setSelectedCity("");
                setSelectedGRType("");
                setGrLevelRows([]);
                setCurrentView("city");

                var payload = {
                  tab: "gurgaon",
                  warehouse: (hub && hub.warehouse) || "",
                  hub: hubName,
                  from_date: fromDate,
                  to_date: toDate,
                };

                await fetchCityLevelDataForHub(payload);
              }}
            >
              {hubName}
            </button>
          );
        },
      },

      // Hub operator (current operator from hub_operator_master)
      {
        headerName: "Hub Operator",
        field: "hub_operator_code",
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        width: 150,
      },

      // Performance bucket (Excellent/Good/Poor) based on within_120h/total_grs
      {
        headerName: "Performance",
        colId: "performance_bucket",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        valueGetter: function (params) {
          var row = params && params.data ? params.data : {};
          var total = n(row.total_grs);
          var within = n(row.within_120h);
          var pct = total ? (within / total) * 100 : 0;

          return performanceLabel(pct, perfConfig);
        },
        cellRenderer: function (params) {
          var v = params && params.value ? params.value : "";
          var perfDot = performanceFillForLabel(v);
          return (
            '<span class="inlineCenter">' +
            '<span class="dot ' + perfDot + '"></span>' +
            '<span class="bold">' + v + "</span>" +
            "</span>"
          );
        },
      },

      // Within 120 Hours Delivery (bucket filter + progress UI)
      {
        headerName: "Within 120 Hours Delivery",
        colId: "within_120_bucket",
        minWidth: 250,
        filter: "agSetColumnFilter",
        filterParams: pctSetFilterParams,

        valueGetter: function (params) {
          var row = params && params.data ? params.data : {};
          var total = Number(row.total_grs);
          if (!isFinite(total)) total = 0;

          var within = Number(row.within_120h);
          if (!isFinite(within)) within = 0;

          var pct = total > 0 ? (within / total) * 100 : 0;
          if (!isFinite(pct)) pct = 0;

          return pctBucket(pct);
        },

        cellRenderer: function (params) {
          var row = params && params.data ? params.data : {};
          var total = Number(row.total_grs);
          if (!isFinite(total)) total = 0;

          var within = Number(row.within_120h);
          if (!isFinite(within)) within = 0;

          var pct = total > 0 ? (within / total) * 100 : 0;
          if (!isFinite(pct)) pct = 0;

          var perfDot = performanceFill(pct, perfConfig);

          // Tooltip text
          var tip = within + " of " + total;
          tip = String(tip).replace(/"/g, "&quot;");

          return (
            '<div class="miniBlock" title="' + tip + '">' +
            '<div class="bold">' + pct.toFixed(1) + '%</div>' +
            '<div class="miniBar">' +
            '<div class="miniBarFill ' + perfDot + '" style="width:' + Math.min(pct, 100) + '%"></div>' +
            "</div>" +
            "</div>"
          );
        }
      }
      ,

      // >120 Hours Delivery (bucket filter + progress UI)
      {
        headerName: ">120 hours Delivery",
        colId: "above_120_bucket",
        minWidth: 230,
        filter: "agSetColumnFilter",
        filterParams: pctSetFilterParams,

        valueGetter: function (params) {
          var row = (params && params.data) ? params.data : {};

          var total = Number(row.total_grs);
          if (!isFinite(total)) total = 0;

          var within = Number(row.within_120h);
          if (!isFinite(within)) within = 0;

          var over = total > 0 ? (total - within) : 0;
          var pct = total > 0 ? (over / total) * 100 : 0;
          if (!isFinite(pct)) pct = 0;

          return pctBucket(pct);
        },

        cellRenderer: function (params) {
          var row = (params && params.data) ? params.data : {};

          var total = Number(row.total_grs);
          if (!isFinite(total)) total = 0;

          var within = Number(row.within_120h);
          if (!isFinite(within)) within = 0;

          var over = total > 0 ? (total - within) : 0;
          var pct = total > 0 ? (over / total) * 100 : 0;
          if (!isFinite(pct)) pct = 0;

          // Tooltip text
          var tip = over + " of " + total;
          tip = String(tip).replace(/"/g, "&quot;");

          return (
            '<div class="miniBlock" title="' + tip + '">' +
            '<div class="bold">' + pct.toFixed(1) + '%</div>' +
            '<div class="miniBar">' +
            '<div class="miniBarFill redFill" style="width:' + Math.min(pct, 100) + '%"></div>' +
            "</div>" +
            "</div>"
          );
        }
      }
      ,

      // Avg Transit Time
      {
        headerName: "Avg. Transit Time",
        field: "avg_transit_time",
        width: 150,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber purpleText">' + val.toFixed(1) + 'h</div>' +
            "</div>"
          );
        },

      },

      // Avg First Mile Transit Time
      {
        headerName: "Avg. First Mile Transit time",
        field: "avg_first_mile_transit_time",
        width: 180,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber indigo">' + val.toFixed(1) + 'h</div>' +
            '</div>'
          );
        },

      },

      // Avg Second Mile Transit Time
      {
        headerName: "Avg. Second Mile Transit time",
        field: "avg_last_mile_transit_time",
        width: 190,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber cyan">' + val.toFixed(1) + 'h</div>' +
            '</div>'
          );
        },

      },

      // Total GRs
      {
        headerName: "Total GRs",
        field: "total_grs",
        width: 120,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber blueText">' + val + '</div>' +
            '</div>'
          );
        },

      },

      // Delivered GRs
      {
        headerName: "Delivered GRs",
        field: "delivered_grs",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber greenText">' + val + '</div>' +
            '</div>'
          );
        },

      },

      // GR within 90 Hours
      {
        headerName: "GR within 90 Hours",
        field: "delivered_within_90h",
        width: 160,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber blueText">' + val + '</div>' +
            '</div>'
          );
        },

      },

      // In Transit
      {
        headerName: "Intransit GR",
        field: "in_transit_count",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber orangeText">' + val + '</div>' +
            '</div>'
          );
        },

      },
      {
        headerName: "Invalid Transit Time GRs",
        field: "invalid_grs",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber redText">' + val + '</div>' +
            '</div>'
          );
        },

      },

      // Truck Cost
      {
        headerName: "Truck Cost(₹)",
        field: "truck_cost",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: moneyCell("blueText")
      },

      // Courier Cost
      {
        headerName: "Courier Cost(₹)",
        field: "courier_cost",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: moneyCell("blueText")
      },

      // Savings
      {
        headerName: "Savings(₹)",
        field: "savings",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: savingsCell()
      },

    ];
  }
  // All Hub Overview AG-Grid ColumnDefs (same UI logic, but 48h/24h)
  function getAllHubColumnDefs() {
    return [
      // Warehouse badge
      {
        headerName: "Warehouse",
        field: "warehouse",
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        width: 140,
        cellRenderer: function (params) {
          var value = params && params.value ? params.value : "";
          return (
            '<div class="badgeBox blueSoft">' +
            '<span class="badgeText blueText">' + value + "</span>" +
            "</div>"
          );
        },
      },

      // Hub clickable (KEEP YOUR EXISTING CLICK LOGIC)
      {
        headerName: "Hub",
        field: "hub_location",
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        width: 160,
        cellRendererFramework: function (params) {
          var hub = params && params.data ? params.data : null;
          var hubName = hub && hub.hub_location ? hub.hub_location : "";

          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                // ✅ keep existing exact behavior
                setSelectedHub(hubName);
                setSelectedWarehouseName((hub && hub.warehouse) || "");
                setSelectedCity("");
                setSelectedGRType("");
                setGrLevelRows([]);
                setCurrentView("city");

                var payload = {
                  warehouse: (hub && hub.warehouse) || "",
                  hub: hubName,
                  from_date: fromDate,
                  to_date: toDate,
                };

                await fetchCityLevelDataForHub(payload);
              }}
            >
              {hubName}
            </button>
          );
        },
      },

      // Hub operator (current operator from hub_operator_master)
      {
        headerName: "Hub Operator",
        field: "hub_operator_code",
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        width: 150,
      },

      // Performance bucket (Excellent/Good/Poor) based on within_48h/total_grs
      {
        headerName: "Performance",
        colId: "performance_bucket",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        valueGetter: function (params) {
          var row = params && params.data ? params.data : {};
          var total = n(row.total_grs);
          var within = n(row.within_48h);
          var pct = total ? (within / total) * 100 : 0;

          return performanceLabel(pct, perfConfig);
        },
        cellRenderer: function (params) {
          var v = params && params.value ? params.value : "";
          var perfDot = performanceFillForLabel(v);
          return (
            '<span class="inlineCenter">' +
            '<span class="dot ' + perfDot + '"></span>' +
            '<span class="bold">' + v + "</span>" +
            "</span>"
          );
        },
      },

      // Within 48 Hours Delivery (bucket filter + progress UI)
      {
        headerName: "Within 48 Hours Delivery",
        colId: "within_48_bucket",
        minWidth: 250,
        filter: "agSetColumnFilter",
        filterParams: pctSetFilterParams,

        valueGetter: function (params) {
          var row = (params && params.data) ? params.data : {};

          var total = Number(row.total_grs);
          if (!isFinite(total)) total = 0;

          var within = Number(row.within_48h);
          if (!isFinite(within)) within = 0;

          var pct = total > 0 ? (within / total) * 100 : 0;
          if (!isFinite(pct)) pct = 0;

          return pctBucket(pct);
        },

        cellRenderer: function (params) {
          var row = (params && params.data) ? params.data : {};

          var total = Number(row.total_grs);
          if (!isFinite(total)) total = 0;

          var within = Number(row.within_48h);
          if (!isFinite(within)) within = 0;

          var pct = total > 0 ? (within / total) * 100 : 0;
          if (!isFinite(pct)) pct = 0;

          var perfDot = performanceFill(pct, perfConfig);

          // Tooltip text (shows on hover)
          var tip = within + " of " + total;
          tip = String(tip).replace(/"/g, "&quot;");

          return (
            '<div class="miniBlock" title="' + tip + '">' +
            '<div class="bold">' + pct.toFixed(1) + '%</div>' +
            '<div class="miniBar">' +
            '<div class="miniBarFill ' + perfDot + '" style="width:' + Math.min(pct, 100) + '%"></div>' +
            "</div>" +
            "</div>"
          );
        }
      }
      ,

      // >48 Hours Delivery (bucket filter + progress UI)
      {
        headerName: ">48 hours Delivery",
        colId: "above_48_bucket",
        minWidth: 230,
        filter: "agSetColumnFilter",
        filterParams: pctSetFilterParams,

        valueGetter: function (params) {
          var row = (params && params.data) ? params.data : {};

          var total = Number(row.total_grs);
          if (!isFinite(total)) total = 0;

          var within = Number(row.within_48h);
          if (!isFinite(within)) within = 0;

          var over = total > 0 ? (total - within) : 0;
          var pct = total > 0 ? (over / total) * 100 : 0;
          if (!isFinite(pct)) pct = 0;

          return pctBucket(pct);
        },

        cellRenderer: function (params) {
          var row = (params && params.data) ? params.data : {};

          var total = Number(row.total_grs);
          if (!isFinite(total)) total = 0;

          var within = Number(row.within_48h);
          if (!isFinite(within)) within = 0;

          var over = total > 0 ? (total - within) : 0;
          var pct = total > 0 ? (over / total) * 100 : 0;
          if (!isFinite(pct)) pct = 0;

          // Tooltip text
          var tip = over + " of " + total;
          tip = String(tip).replace(/"/g, "&quot;");

          return (
            '<div class="miniBlock" title="' + tip + '">' +
            '<div class="bold">' + pct.toFixed(1) + '%</div>' +
            '<div class="miniBar">' +
            '<div class="miniBarFill redFill" style="width:' + Math.min(pct, 100) + '%"></div>' +
            "</div>" +
            "</div>"
          );
        }
      }
      ,

      // Avg Transit Time
      {
        headerName: "Avg. Transit Time",
        field: "avg_transit_time",
        width: 150,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber purpleText">' + val.toFixed(1) + 'h</div>' +
            "</div>"
          );
        },
      },

      // Avg First Mile Transit Time
      {
        headerName: "Avg. First Mile Transit time",
        field: "avg_first_mile_transit_time",
        width: 180,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber indigo">' + val.toFixed(1) + 'h</div>' +
            '</div>'
          );
        },
      },

      // Avg Second Mile Transit Time
      {
        headerName: "Avg. Second Mile Transit time",
        field: "avg_last_mile_transit_time",
        width: 190,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber cyan">' + val.toFixed(1) + 'h</div>' +
            '</div>'
          );
        },
      },

      // Total GRs
      {
        headerName: "Total GRs",
        field: "total_grs",
        width: 120,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber blueText">' + val + '</div>' +
            '</div>'
          );
        },

      },

      // Delivered GRs
      {
        headerName: "Delivered GRs",
        field: "delivered_grs",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber greenText">' + val + '</div>' +
            '</div>'
          );
        },

      },

      // GR within 24 Hours
      {
        headerName: "GR within 24 Hours",
        field: "delivered_within_24h",
        width: 170,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber blueText">' + val + '</div>' +
            '</div>'
          );
        },

      },

      // In Transit
      {
        headerName: "In Transit GR",
        field: "in_transit_count",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber orangeText">' + val + '</div>' +
            '</div>'
          );
        },

      },
      {
        headerName: "Invalid Transit Time GRs",
        field: "invalid_grs",
        width: 140,
        filter: "agSetColumnFilter",
        filterParams: setFilterParams,
        cellRenderer: function (params) {
          var val = Number(params && params.value);
          if (!isFinite(val)) val = 0;

          return (
            '<div class="tc">' +
            '<div class="bigNumber redText">' + val + '</div>' +
            '</div>'
          );
        },

      },

      // Truck Cost
      // {
      //   headerName: "Truck Cost(₹)",
      //   field: "truck_cost",
      //   width: 140,
      //   filter: "agSetColumnFilter",
      //   filterParams: setFilterParams,
      //   cellRenderer: moneyCell("blueText")
      // },

      // Courier Cost
      // {
      //   headerName: "Courier Cost(₹)",
      //   field: "courier_cost",
      //   width: 140,
      //   filter: "agSetColumnFilter",
      //   filterParams: setFilterParams,
      //   cellRenderer: moneyCell("blueText")
      // },

      // Savings
      // {
      //   headerName: "Savings(₹)",
      //   field: "savings",
      //   width: 140,
      //   filter: "agSetColumnFilter",
      //   filterParams: setFilterParams,
      //   cellRenderer: savingsCell()
      // },
    ];
  }


  var trendData = useMemo(
    function () {
      return trendApiAll.slice(0)
    },
    [trendApiAll],
  )

  var gurgoanTrendData = useMemo(
    function () {
      return trendApiGurgaon.slice(0)
    },
    [trendApiGurgaon],
  )

  function MiniCardsFromTrend(props) {
    var d = props.data || []
    var labelShown = props.labelShown || "Total Hubs"
    var summary = useMemo(
      function () {
        return computeSummaryFromTrend(d, 5, activeSubTab)
      },
      [d],
    )

    return (
      <div className="miniCards">
        <div className="miniCard">
          <div className="miniCardLabel">{labelShown}</div>
          <div className="miniCardValue">{summary.shownCount}</div>
        </div>

        <div className="miniCard">
          <div className="miniCardLabel">Best Performance</div>
          <div className="miniCardValue" style={{ color: "#2e7d32" }}>
            {summary.bestHub ? summary.bestHub + " - " + summary.bestAvg.toFixed(2) + "h" : "N/A"}
          </div>
        </div>

        <div className="miniCard">
          <div className="miniCardLabel">Poor Performance</div>
          <div className="miniCardValue" style={{ color: "#d32f2f" }}>
            {summary.poorHub ? summary.poorHub + " - " + summary.poorAvg.toFixed(2) + "h" : "N/A"}
          </div>
        </div>
      </div>
    )
  }



  async function submitDateFilters() {

    await fetchData()

    const isAllTab = activeSubTab === "all"
    const type = isAllTab ? "all" : "gurgaon"
    const timeRange = isAllTab ? timeRangeAll : timeRangeGurgaon
    const hub = isAllTab ? trendHub : gurgoanTrendHub
    const warehouse = isAllTab ? trendWarehouse : gurgaonWarehouse

    const tdata = await fetchTrendData(type, timeRange, hub, warehouse, selectedWarehouses)
    if (isAllTab) setTrendApiAll(tdata)
    else setTrendApiGurgaon(tdata)

    if (
      selectedWarehouses &&
      selectedWarehouses.length === 1 &&
      selectedWarehouses[0].value !== "all"
    ) {
      if (activeSubTab === "all") {
        setTrendWarehouse(selectedWarehouses[0].value)
      } else {
        setGurgaonWarehouse(selectedWarehouses[0].value)
      }
    } else {
      if (activeSubTab === "all") {
        setTrendWarehouse("all")
      } else {
        setGurgaonWarehouse("all")
      }
    }


    setCityLevelRows([])
    setGurgaonCityLevelRows([])
    setGrLevelRows([])
    setSelectedCity("")
    setSelectedGRType("")
    setSelectedDealerCode("")
    setSelectedGRNumber("")
    setSelectedDeliveryTime("")
  }
  function getTrendHubOptionsForAllTab() {
    // If multiple warehouses selected OR "all", show all hubs combined
    var wh = selectedWarehouses || [{ value: "all" }];
    var values = wh.map(function (x) { return x.value; });

    // if all or multi-select -> union of all hubs
    var hubs = [];
    if (values.indexOf("all") !== -1 || values.length !== 1) {
      var keys = Object.keys(warehouseHubMap);
      for (var i = 0; i < keys.length; i++) {
        var arr = warehouseHubMap[keys[i]] || [];
        for (var j = 0; j < arr.length; j++) hubs.push(arr[j]);
      }
    } else {
      // exactly one warehouse selected
      hubs = warehouseHubMap[values[0]] || [];
    }

    // unique + sort
    var seen = {};
    var out = [];
    for (var k = 0; k < hubs.length; k++) {
      var name = hubs[k];
      if (name && !seen[name]) {
        seen[name] = true;
        out.push(name);
      }
    }
    return out.sort();
  }
  function getMasterTab(tab) {
    if (tab === "gurgaon") {
      return "gurgaon";
    }
    return "all_warehouses";
  }
  function getGrLevelColumnDefs() {
    return [
      {
        headerName: "GR No",
        field: "grNo",
        minWidth: 130,
        pinned: "left",
      },
      {
        headerName: "Dealer Code",
        field: "dealerCode",
        minWidth: 150,
      },
      {
        headerName: "Dealer Name",
        field: "dealerName",
        minWidth: 180,
      },
      {
        headerName: "WRHS",
        field: "wrhs",
        minWidth: 120,
      },
      {
        headerName: "Hub",
        field: "hub",
        minWidth: 120,
      },
      {
        headerName: "Hub Operator Code",
        field: "hubOperatorCode",
        minWidth: 150,
      },
      {
        headerName: "Hub Operator Name",
        field: "hubOperatorName",
        minWidth: 190,
      },
      {
        headerName: "City",
        field: "city",
        minWidth: 120,
      },
      {
        headerName: "MSIL Gateout Date & Time",
        field: "gateOutDateTime",
        minWidth: 190,
      },
      {
        headerName: "Hub Reached Date & Time",
        field: "hubReachedDateTime",
        minWidth: 190,
      },
      {
        headerName: "Hub left Date & Time",
        field: "hubLeftDateTime",
        minWidth: 190,
      },
      {
        headerName: "Consignee Delivery Date & Time",
        field: "deliveryDateTime",
        minWidth: 210,
        // wrapHeaderText: true,
        // autoHeaderHeight: true,
      },
      {
        headerName: "Delivery Duration (Hr)",
        field: "deliveryDuration",
        minWidth: 170,
        cellStyle: { textAlign: "center" },
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
      {
        headerName: "First Mile (Hr)",
        field: "firstMile",
        minWidth: 140,
        cellStyle: { textAlign: "center" },
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
      {
        headerName: "Last Mile (Hr)",
        field: "lastMile",
        minWidth: 140,
        cellStyle: { textAlign: "center" },
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
      {
        headerName: "Truck Cost (₹)",
        field: "truckCost",
        minWidth: 140,
        valueFormatter: function (params) {
          return params.value != null && !isNaN(params.value)
            ? Number(params.value).toFixed(2)
            : "0.00";
        },
        cellRenderer: function (params) {
          return '<span class="blueText">' + (
            params.value != null && !isNaN(params.value)
              ? Number(params.value).toFixed(2)
              : "0.00"
          ) + "</span>";
        },
      },
      {
        headerName: "Courier Cost (₹)",
        field: "courierCost",
        minWidth: 150,
        valueFormatter: function (params) {
          return params.value != null && !isNaN(params.value)
            ? Number(params.value).toFixed(2)
            : "0.00";
        },
        cellRenderer: function (params) {
          return '<span class="blueText">' + (
            params.value != null && !isNaN(params.value)
              ? Number(params.value).toFixed(2)
              : "0.00"
          ) + "</span>";
        },
      },
      {
        headerName: "Savings (₹)",
        field: "savings",
        minWidth: 130,
        valueFormatter: function (params) {
          return params.value != null && !isNaN(params.value)
            ? Number(params.value).toFixed(2)
            : "0.00";
        },
        cellRenderer: function (params) {
          return '<span class="greenText">' + (
            params.value != null && !isNaN(params.value)
              ? Number(params.value).toFixed(2)
              : "0.00"
          ) + "</span>";
        },
      },
    ];
  }
  function getHubGrLevelColumnDefs() {
    return [
      {
        headerName: "GR No",
        field: "grNo",
        minWidth: 130,
        pinned: "left",
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Dealer Code",
        field: "dealerCode",
        minWidth: 140,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Dealer Name",
        field: "dealerName",
        minWidth: 220,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "WRHS",
        field: "wrhs",
        minWidth: 120,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Hub",
        field: "hub",
        minWidth: 120,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Hub Operator Code",
        field: "hubOperatorCode",
        minWidth: 150,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Hub Operator Name",
        field: "hubOperatorName",
        minWidth: 190,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "City",
        field: "city",
        minWidth: 120,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "MSIL Gateout Date & Time",
        field: "gateOutDateTime",
        minWidth: 190,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Hub Reached Date & Time",
        field: "hubReachedDateTime",
        minWidth: 190,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Hub left Date & Time",
        field: "hubLeftDateTime",
        minWidth: 190,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Consignee Delivery Date and Time",
        field: "deliveryDateTime",
        minWidth: 220,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Delivery Duration (Hr)",
        field: "deliveryDuration",
        minWidth: 170,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
      {
        headerName: "First Mile (Hr)",
        field: "firstMile",
        minWidth: 140,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
      {
        headerName: "Last Mile (Hr)",
        field: "lastMile",
        minWidth: 140,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
    ];
  }
  function getGurgaonCityLevelColumnDefs() {
    return [
      {
        headerName: "City",
        field: "city",
        minWidth: 120,
        maxWidth: 140
      },
      {
        headerName: "Hub Location",
        field: "hub_location_display",
        minWidth: 120,
        maxWidth: 160,
        valueGetter: function () {
          return regionLabel(selectedHub);
        },
      },
      {
        headerName: "Warehouse",
        field: "warehouse",
        minWidth: 120,
        maxWidth: 120,
        valueGetter: function (params) {
          return params.data.warehouse || selectedWarehouseName || "";
        },
      },
      {
        headerName: "Total GRs",
        field: "totalGRs",
        minWidth: 80,
        maxWidth: 80,
        cellStyle: { textAlign: "center" },
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("total");
                setCurrentView("gr");

                var payload = {
                  tab: "gurgaon",
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: "total_grs",
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.totalGRs}
            </button>
          );
        },
      },
      {
        headerName: "Delivered GRs",
        field: "deliveredGRs",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("delivered");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  tab: "gurgaon",
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("delivered", "gurgaon"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.deliveredGRs}
            </button>
          );
        },
      },
      {
        headerName: "Transit GRs",
        field: "transitGRs",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("transit");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  tab: "gurgaon",
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("transit", "gurgaon"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.transitGRs}
            </button>
          );
        },
      },
      {
        headerName: "Invalid Transit Time GRs",
        field: "invalid_grs",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("invalid");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  tab: "gurgaon",
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("invalid", "gurgaon"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.invalid_grs}
            </button>
          );
        },
      },
      {
        headerName: "GRs within 90 Hr",
        field: "grsWithin90Hr",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("within90hr");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  tab: "gurgaon",
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("within90hr", "gurgaon"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.grsWithin90Hr}
            </button>
          );
        },
      },
      {
        headerName: "GRs within 90-120 Hr",
        field: "grsWithin90_120Hr",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("within90_120hr");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  tab: "gurgaon",
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("within90_120hr", "gurgaon"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.grsWithin90_120Hr}
            </button>
          );
        },
      },
      {
        headerName: "GRs within 120 Hr",
        field: "grsWithin120Hr",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("within120hr");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  tab: "gurgaon",
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("within120hr", "gurgaon"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.grsWithin120Hr}
            </button>
          );
        },
      },
      {
        headerName: ">120 Hr GRs",
        field: "grsOver120Hr",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("over120hr");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  tab: "gurgaon",
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("over120hr", "gurgaon"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.grsOver120Hr}
            </button>
          );
        },
      },
      {
        headerName: "% 90 Hr Delivery",
        field: "percent90Hr",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1) + "%";
        },
        cellRenderer: function (params) {
          return '<span class="pct pctGreen">' + Number(params.value || 0).toFixed(1) + "%</span>";
        },
      },
      {
        headerName: "% 90-120 Hr Delivery",
        field: "percentWithin90_120Hr",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1) + "%";
        },
        cellRenderer: function (params) {
          return '<span class="pct pctGreen">' + Number(params.value || 0).toFixed(1) + "%</span>";
        },
      },
      {
        headerName: "% 120 Hr Delivery",
        field: "percent120Hr",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1) + "%";
        },
        cellRenderer: function (params) {
          return '<span class="pct pctGreen">' + Number(params.value || 0).toFixed(1) + "%</span>";
        },
      },
      {
        headerName: "% > 120 Hr Delivery",
        field: "percentOver120Hr",
        minWidth: 120,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1) + "%";
        },
        cellRenderer: function (params) {
          return '<span class="pct pctGreen">' + Number(params.value || 0).toFixed(1) + "%</span>";
        },
      },
      {
        headerName: "Transit Time - First Mile (Hr)",
        field: "firstMileTime",
        minWidth: 130,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
      {
        headerName: "Transit Time - Last Mile (Hr)",
        field: "lastMileTime",
        minWidth: 130,
        maxWidth: 160,
        cellStyle: { textAlign: "center" },
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
      {
        headerName: "Truck Cost (₹)",
        field: "truck_cost",
        minWidth: 100,
        maxWidth: 130,
        cellStyle: { textAlign: "center" },
        cellRenderer: function (params) {
          return '<button class="linkBtn" type="button">' + (params.value || "0.00") + "</button>";
        },
      },
      {
        headerName: "Courier Cost (₹)",
        field: "courier_cost",
        minWidth: 100,
        maxWidth: 130,
        cellStyle: { textAlign: "center" },
        cellRenderer: function (params) {
          return '<button class="linkBtn" type="button">' + (params.value || "0.00") + "</button>";
        },
      },
      {
        headerName: "Savings (₹)",
        field: "savings",
        minWidth: 100,
        maxWidth: 130,
        cellStyle: { textAlign: "center" },
        cellRenderer: function (params) {
          return '<button class="linkBtn pctGreen" type="button">' + (params.value || "0.00") + "</button>";
        },
      },
    ];
  }
  function getAllCityLevelColumnDefs() {
    return [
      {
        headerName: "City",
        field: "city",
        width: 110,
        minWidth: 100,
        maxWidth: 140,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
      },
      {
        headerName: "Hub Location",
        field: "hub_location_display",
        width: 130,
        minWidth: 120,
        maxWidth: 160,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
        valueGetter: function () {
          return regionLabel(selectedHub);
        },
      },
      {
        headerName: "Warehouse",
        field: "warehouse",
        width: 130,
        minWidth: 120,
        maxWidth: 160,
        headerClass: "ag-left-header",
        cellClass: "ag-left-cell",
        valueGetter: function (params) {
          return params && params.data
            ? (params.data.warehouse || selectedWarehouseName || "")
            : "";
        },
      },
      {
        headerName: "Total GRs",
        field: "totalGRs",
        width: 110,
        minWidth: 100,
        maxWidth: 130,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("total");
                setCurrentView("gr");

                var payload = {
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: "total_grs",
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.totalGRs}
            </button>
          );
        },
      },
      {
        headerName: "Delivered GRs",
        field: "deliveredGRs",
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("delivered");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("delivered", "all"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.deliveredGRs}
            </button>
          );
        },
      },
      {
        headerName: "Transit GRs",
        field: "transitGRs",
        width: 110,
        minWidth: 100,
        maxWidth: 130,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("transit");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("transit", "all"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.transitGRs}
            </button>
          );
        },
      },
      {
        headerName: "Invalid Transit Time GRs",
        field: "invalid_grs",
        width: 150,
        minWidth: 140,
        maxWidth: 180,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("invalid");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("invalid", "all"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.invalid_grs}
            </button>
          );
        },
      },
      {
        headerName: "GRs within 24 Hr",
        field: "grsWithin24Hr",
        width: 130,
        minWidth: 120,
        maxWidth: 150,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("within24hr");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("within24hr", "all"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.grsWithin24Hr}
            </button>
          );
        },
      },
      {
        headerName: "GRs within 24-48 Hr",
        field: "grsWithin24_48Hr",
        width: 145,
        minWidth: 135,
        maxWidth: 170,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("within24_48hr");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("within24_48hr", "all"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.grsWithin24_48Hr}
            </button>
          );
        },
      },
      {
        headerName: "GRs within 48 Hr",
        field: "grsWithin48Hr",
        width: 130,
        minWidth: 120,
        maxWidth: 150,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("within48hr");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("within48hr", "all"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.grsWithin48Hr}
            </button>
          );
        },
      },
      {
        headerName: ">48 Hr GRs",
        field: "grsOver48Hr",
        width: 110,
        minWidth: 100,
        maxWidth: 130,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        cellRendererFramework: function (params) {
          var city = params.data;
          return (
            <button
              className="linkBtn"
              type="button"
              onClick={async function () {
                setSelectedCity(city.city);
                setSelectedGRType("over48hr");
                setSelectedDealerCode("");
                setSelectedGRNumber("");
                setSelectedDeliveryTime("");
                setCurrentView("gr");

                var payload = {
                  warehouse: city.warehouse || selectedWarehouseName || "",
                  hub: selectedHub,
                  city: city.city,
                  gr_status: mapGrTypeToStatusKey("over48hr", "all"),
                  from_date: toIsoStartOfDay(fromDate),
                  to_date: toIsoStartOfDay(toDate),
                };

                await fetchGrLevelData(payload);
              }}
            >
              {city.grsOver48Hr}
            </button>
          );
        },
      },
      {
        headerName: "% 24 Hr Delivery",
        field: "percent24Hr",
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1) + "%";
        },
        cellRenderer: function (params) {
          return '<span class="pct pctGreen">' + Number(params.value || 0).toFixed(1) + "%</span>";
        },
      },
      {
        headerName: "% 48 Hr Delivery",
        field: "percent48Hr",
        width: 120,
        minWidth: 110,
        maxWidth: 140,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1) + "%";
        },
        cellRenderer: function (params) {
          return '<span class="pct pctGreen">' + Number(params.value || 0).toFixed(1) + "%</span>";
        },
      },
      {
        headerName: "% 24-48 Hr Delivery",
        field: "percentWithin24_48Hr",
        width: 140,
        minWidth: 130,
        maxWidth: 165,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1) + "%";
        },
        cellRenderer: function (params) {
          return '<span class="pct pctGreen">' + Number(params.value || 0).toFixed(1) + "%</span>";
        },
      },
      {
        headerName: "% > 48 Hr Delivery",
        field: "percentOver48Hr",
        width: 125,
        minWidth: 115,
        maxWidth: 145,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1) + "%";
        },
        cellRenderer: function (params) {
          return '<span class="pct pctGreen">' + Number(params.value || 0).toFixed(1) + "%</span>";
        },
      },
      {
        headerName: "Transit Time - First Mile (Hr)",
        field: "firstMileTime",
        width: 155,
        minWidth: 145,
        maxWidth: 180,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
      {
        headerName: "Transit Time - Last Mile (Hr)",
        field: "lastMileTime",
        width: 155,
        minWidth: 145,
        maxWidth: 180,
        headerClass: "ag-center-header",
        cellClass: "ag-center-cell",
        valueFormatter: function (params) {
          return Number(params.value || 0).toFixed(1);
        },
      },
    ];
  }


  function handleWarehouseChange(selected, actionMeta) {
    var values = selected || [];

    // nothing selected -> fallback to All
    if (values.length === 0) {
      setSelectedWarehouses([{ label: "All", value: "all" }]);
      return;
    }

    var lastPicked =
      actionMeta && actionMeta.option ? actionMeta.option.value : null;

    // if user explicitly selected All, keep only All
    if (lastPicked === "all") {
      setSelectedWarehouses([{ label: "All", value: "all" }]);
      return;
    }

    // if user selected any other option, remove All
    values = values.filter(function (item) {
      return item.value !== "all";
    });

    setSelectedWarehouses(values);
  }


  return (
    <div className="ddPage">
      {/* ALL TAB */}
      {activeSubTab === "all" && (
        <div className="ddSection">
          {/* Header */}
          {currentView !== "hub" ? (
            <DashboardHeaderBanner>
              <div className="ddHeaderRow">
                <div className="ddHeaderLeft">
                  <h2 className="ddSubtitle">All Warehouses</h2>
                </div>

                <div className="ddHeaderRight">
                  <div className="ddRefreshText">Last refreshed: {formatRefreshTime(lastRefreshTime)}</div>
                  <button className="btn btnOutline" onClick={handleBack} type="button">
                    <span className="btnIcon">←</span>
                    Back
                  </button>
                </div>
              </div>
            </DashboardHeaderBanner>
          ) : (
            <div>
              <DashboardHeaderBanner>
                <div className="ddHeaderRow ddHeaderCompact">
                  <h2 className="ddSubtitle">All Warehouses</h2>
                  <div className="ddRefreshText">Last refreshed: {formatRefreshTime(lastRefreshTime)}</div>
                </div>
              </DashboardHeaderBanner>
              <div className="hmCardBody hmCardBodyCompact" style={{ paddingLeft: "0px" }}>
                <div className="hmFilterGridStyle">
                  <div className="hmField">
                    <label className="hmLabel" htmlFor="from-date">
                      From Date
                    </label>
                    <input
                      id="from-date"
                      className="hmInput"
                      type="date"
                      value={fromDate}
                      onChange={function (e) {
                        setFromDate(e.target.value)
                      }}
                    />
                  </div>

                  <div className="hmField">
                    <label className="hmLabel" htmlFor="to-date">
                      To Date
                    </label>
                    <input
                      id="to-date"
                      className="hmInput"
                      type="date"
                      value={toDate}
                      onChange={function (e) {
                        setToDate(e.target.value)
                      }}
                    />
                  </div>

                  <div className="hmField">
                    <label className="hmLabel">Warehouse</label>
                    <Select
                      isMulti
                      className="warehouse-select"
                      classNamePrefix="warehouse-select"
                      options={warehousesList}
                      value={selectedWarehouses}
                      onChange={handleWarehouseChange}
                    />
                  </div>

                  <div className="hmField hmFieldBtn">
                    <button className="submit-btn" type="button" onClick={submitDateFilters}>
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {loading &&
            <div className="ddLoadingWrap">
              <div className="ddLoadingText">Loading dashboard...</div>
            </div>}

          {/* Hub Level */}

          {(currentView === "hub" && !loading) && (
            <div className="ddBlock">
              {/* KPI */}
              <div className="kpiGrid">
                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon blueSoft">
                      <svg className="kpiSvg blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-2 0H3m2-16h14" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Total Hubs</div>
                      <div className="kpiValue">{countersData.totalHubs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon blueSoft">
                      <svg className="kpiSvg blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Total GRs</div>
                      <div className="kpiValue">{countersData.totalGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon greenSoft">
                      <svg className="kpiSvg green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Total Delivered</div>
                      <div className="kpiValue">{countersData.deliveredGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon orangeSoft">
                      <svg className="kpiSvg orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">In Transit GRs</div>
                      <div className="kpiValue">{countersData.intransitGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon redSoft">
                      <svg className="kpiSvg red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Invalid GRs</div>
                      <div className="kpiValue">{countersData.invalidGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon yellowSoft">
                      <svg className="kpiSvg yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Hub Not Mapped GRs</div>
                      <div className="kpiValue">{countersData.unmappedHubGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon yellowSoft">
                      <svg className="kpiSvg yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">{"<"}48 Hours GRs</div>
                      <div className="kpiValue">{countersData.within48Hrs} ({countersData.within48HrsPct}%)</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon redSoft">
                      <svg className="kpiSvg red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">&gt;48hr GRs</div>
                      <div className="kpiValue">{countersData.gt48Hrs} ({countersData.gt48HrsPct}%)</div>
                    </div>
                  </div>
                </div>

                {/* <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon greenSoft">
                      ₹
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Savings (₹)</div>
                      <div className="kpiValue">{`${countersData.savings}M`}</div>
                    </div>
                  </div>
                </div> */}
              </div>

              {/* Overview */}
              {showHubOverview && (
                <div className="card">
                  <div className="cardHeader">
                    <div className="cardHeaderRow">
                      <div className="cardTitle blueText">Hub Performance Overview</div>
                      <PerformanceLegend config={perfConfig} />
                    </div>
                  </div>

                  <div className="cardBody">
                    <div className="tableWrap">
                      <div className="ag-theme-balham ddAgGrid ddAgGridBlue" style={{ width: "100%", height: 420 }}>
                        <AgGridReact
                          rowData={hubSummary}
                          columnDefs={getAllHubColumnDefs()}
                          defaultColDef={{
                            sortable: true,
                            filter: "agSetColumnFilter",     // ✅ default = checkbox filter for ALL columns
                            floatingFilter: false,           // screenshot style (filter icon in header menu)
                            resizable: true,
                            minWidth: 120,
                            menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
                            filterParams: {
                              buttons: ["reset", "apply"],
                              closeOnApply: true,
                              suppressMiniFilter: false,
                              refreshValuesOnOpen: true,     // ✅ values list = only from grid rows
                            },
                          }}
                          animateRows={true}
                          headerHeight={45}
                          rowHeight={70}
                          suppressCellFocus={true}
                          enableRangeSelection={true}
                          // ✅ Pagination
                          pagination={true}
                          paginationPageSize={20}
                          paginationPageSizeSelector={[20, 50, 100, 200]}
                          sideBar={{
                            toolPanels: [
                              {
                                id: "columns",
                                labelDefault: "Columns",
                                labelKey: "columns",
                                iconKey: "columns",
                                toolPanel: "agColumnsToolPanel",
                                toolPanelParams: {
                                  suppressPivotMode: true,
                                  suppressRowGroups: true,
                                  suppressValues: true,
                                },
                              },
                              {
                                id: "filters",
                                labelDefault: "Filters",
                                labelKey: "filters",
                                iconKey: "filter",
                                toolPanel: "agFiltersToolPanel",
                              },
                            ],
                          }}
                          statusBar={{
                            statusPanels: [
                              { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                              { statusPanel: "agTotalRowCountComponent", align: "center" },
                              { statusPanel: "agFilteredRowCountComponent" },
                              { statusPanel: "agSelectedRowCountComponent" },
                              { statusPanel: "agAggregationComponent" },
                            ],
                          }}
                        />
                      </div>
                    </div>


                  </div>
                </div>
              )}


              {/* PERFORMANCE COMPARISON  (UNCHANGED) */}
              <div className="card">
                <div className="cardHeader">
                  <div className="cardHeaderRow">
                    <div className="cardTitle">Performance Comparison</div>
                    <div className="btnGroup">
                      <ButtonSimple
                        variant={chartView === "gr" ? "primary" : "outline"}
                        onClick={function () {
                          setChartView("gr")
                        }}
                      >
                        GR
                      </ButtonSimple>
                      <ButtonSimple
                        variant={chartView === "transit" ? "primary" : "outline"}
                        onClick={function () {
                          setChartView("transit")
                        }}
                      >
                        %
                      </ButtonSimple>
                    </div>
                  </div>

                  <div className="legendRow below">
                    <span className="legendItem">
                      <span className="legendBox greenFill" /> Delivered GR
                    </span>
                    <span className="legendItem">
                      <span className="legendBox blueFill" /> Within 24 Hours
                    </span>
                    <span className="legendItem">
                      <span className="legendBox yellowFill" /> 24 to 36 Hours
                    </span>
                    <span className="legendItem">
                      <span className="legendBox orangeFill" /> 36 to 48 Hours
                    </span>
                    <span className="legendItem">
                      <span className="legendBox redFill" /> &gt; 48 Hours
                    </span>
                  </div>
                </div>

                <div className="cardBody">
                  <div className="perfStack">
                    {(perfComparison || [])
                      .slice()
                      .sort(function (a, b) {
                        var aTotal = a.delivered_grs || 1
                        var bTotal = b.delivered_grs || 1

                        var aPerf = ((aTotal - (a.above_48h || 0)) / aTotal) * 100
                        var bPerf = ((bTotal - (b.above_48h || 0)) / bTotal) * 100

                        return bPerf - aPerf
                      })
                      .slice(0, 5)
                      .map(function (hub, index) {
                        var total = hub.delivered_grs || 1

                        var within24 = hub.delivered_within_24h || 0
                        var band24to36 = hub.delivered_in_between_24h_and_36h || 0
                        var band36to48 = hub.delivered_in_between_36h_and_48h || 0
                        var over48 = hub.above_48h || 0

                        var deliveredTopLabel = hub.delivered_grs || 0

                        var pctBlue = (within24 / total) * 100
                        var pctYellow = (band24to36 / total) * 100
                        var pctOrange = (band36to48 / total) * 100
                        var pctRed = (over48 / total) * 100

                        var wBlue = chartView === "gr" ? pctBlue : pctBlue
                        var wYellow = chartView === "gr" ? pctYellow : pctYellow
                        var wOrange = chartView === "gr" ? pctOrange : pctOrange
                        var wRed = chartView === "gr" ? pctRed : pctRed

                        function segLabel(value, pct) {
                          if (chartView === "gr") {
                            if (pct < 6) return ""
                            return String(value) + "(" + pct.toFixed(0) + "%)"
                          } else {
                            if (pct < 6) return ""
                            return pct.toFixed(0) + "%"
                          }
                        }

                        return (
                          <div className="perfBlock" key={`${hub.warehouse}-${index}`}>
                            <div className="perfHubTitle">{hub.warehouse}</div>

                            <div className="perfRow">
                              <div className="perfTopBar">
                                <div className="perfTopFill greenFill">
                                  <span className="barText">{deliveredTopLabel}</span>
                                </div>
                              </div>

                              <div className="perfBottomBar">
                                <div className="seg blueFill" style={{ width: wBlue + "%" }}>
                                  <span className="barText">{segLabel(within24, pctBlue)}</span>
                                </div>

                                <div className="seg yellowFill" style={{ width: wYellow + "%" }}>
                                  <span className="barText">{segLabel(band24to36, pctYellow)}</span>
                                </div>

                                <div className="seg orangeFill" style={{ width: wOrange + "%" }}>
                                  <span className="barText">{segLabel(band36to48, pctOrange)}</span>
                                </div>

                                <div className="seg redFill" style={{ width: wRed + "%" }}>
                                  <span className="barText">{segLabel(over48, pctRed)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              </div>

              {/* Transit Time Trend */}
              <div className="card">
                <div className="cardHeader">
                  <div className="cardHeaderRow">
                    <div className="cardTitle">Transit Time Trend</div>

                    <div className="filterRow">
                      <SelectSimple
                        value={trendWarehouse}
                        onChange={function (e) {
                          setTrendWarehouse(e.target.value)
                        }}
                      >
                        <option value="all">All Warehouses</option>
                        {Array.from(new Set(hubSummary.map((h) => h.warehouse).filter((h) => h && String(h).trim() !== ""))).map((w) => (
                          <option key={w} value={w}>
                            {w}
                          </option>
                        ))}
                      </SelectSimple>

                      <SelectSimple
                        value={trendHub}
                        onChange={function (e) { setTrendHub(e.target.value); }}
                      >
                        <option value="all">All Hubs</option>
                        {getTrendHubOptionsForAllTab().map(function (h) {
                          return (
                            <option key={h} value={h}>
                              {h}
                            </option>
                          );
                        })}
                      </SelectSimple>


                      <SelectSimple
                        value={timeRangeAll}
                        onChange={function (e) {
                          setTimeRangeAll(e.target.value)
                        }}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </SelectSimple>
                    </div>
                  </div>
                </div>

                <div className="cardBody">
                  <div className="chartBox">
                    {trendLoading ? (
                      <div className="trendLoaderWrap">
                        <div className="trendLoader">Loading trend...</div>
                      </div>
                    ) : (
                      <TransitTimeTrendChart
                        data={trendData}
                        timePeriod={timeRangeAll}
                        warehouseType="all"
                      />
                    )}
                  </div>

                  <MiniCardsFromTrend data={trendData} labelShown="Total Hubs" />
                </div>
              </div>
            </div>
          )}

          {/* City view (uses python-backed endpoint rows) */}
          {currentView === "city" && selectedHub && (
            <div className="card ddCardPad">
              <div className="ddCardHeaderRow">
                <h3 className="ddCardTitle">Hub-Wise (City-Level) Details - {selectedHub}</h3>
              </div>

              <div className="tableWrap">
                {cityLevelLoading ? (
                  <div className="ddLoaderWrap">
                    <div className="ddLoadingText">Loading city-level data...</div>
                  </div>
                ) : (
                  <div
                    className="ag-theme-balham"
                    style={{ width: "100%", height: 420 }}
                  >
                    <AgGridReact
                      rowData={getCityDetailsFromApiRows()}
                      columnDefs={getAllCityLevelColumnDefs()}
                      defaultColDef={{
                        sortable: true,
                        filter: "agSetColumnFilter",
                        floatingFilter: false,
                        resizable: true,
                        minWidth: 100,
                        menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
                        filterParams: {
                          buttons: ["reset", "apply"],
                          closeOnApply: true,
                          suppressMiniFilter: false,
                          refreshValuesOnOpen: true,
                        },
                      }}
                      animateRows={true}
                      headerHeight={45}
                      rowHeight={45}
                      suppressCellFocus={true}
                      pagination={true}
                      paginationPageSize={20}
                      enableRangeSelection={true}
                      paginationPageSizeSelector={[20, 50, 100, 200]}
                      sideBar={{
                        toolPanels: [
                          {
                            id: "columns",
                            labelDefault: "Columns",
                            labelKey: "columns",
                            iconKey: "columns",
                            toolPanel: "agColumnsToolPanel",
                            toolPanelParams: {
                              suppressPivotMode: true,
                              suppressRowGroups: true,
                              suppressValues: true,
                            },
                          },
                          {
                            id: "filters",
                            labelDefault: "Filters",
                            labelKey: "filters",
                            iconKey: "filter",
                            toolPanel: "agFiltersToolPanel",
                          },
                        ],
                      }}
                      statusBar={{
                        statusPanels: [
                          { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                          { statusPanel: "agTotalRowCountComponent", align: "center" },
                          { statusPanel: "agFilteredRowCountComponent" },
                          { statusPanel: "agSelectedRowCountComponent" },
                          { statusPanel: "agAggregationComponent" },
                        ],
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* GR view (API rows + filters work) */}
          {currentView === "gr" && selectedHub && selectedCity && (
            <div className="card ddCardPad">
              <div className="ddCardHeaderRow">
                <h3 className="ddCardTitle">
                  GR-Level Details - {selectedHub} / {selectedCity} / {selectedGRType}
                </h3>
              </div>

              {/* Filters (UI unchanged) */}
              <div className="filterPanel">
                <div className="filterTop">
                  <div className="filterIcon">⏷</div>
                  <div className="filterTitle">GR Level Filters</div>
                </div>

                <div className="filterGrid">
                  <div className="fieldWrap dealerCodeField">
                    <label className="fieldLabel">Dealer Code</label>
                    <Select
                      className="dealerCodeSelect"
                      classNamePrefix="dealer-code-select"
                      placeholder="Search Dealer Code"
                      value={
                        selectedDealerCode === "all"
                          ? { value: "all", label: "All Dealer Codes" }
                          : selectedDealerCode
                            ? { value: selectedDealerCode, label: selectedDealerCode }
                            : null
                      }
                      onChange={(selected) =>
                        setSelectedDealerCode(selected ? selected.value : "")
                      }
                      options={[
                        { value: "all", label: "All Dealer Codes" },
                        ...getAvailableDealerCodes().map((code) => ({
                          value: code,
                          label: code,
                        })),
                      ]}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                        }),
                        control: (base) => ({
                          ...base,
                          minHeight: "44px",
                          width: "100%",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>

                  <div className="fieldWrap grNumberField">
                    <label className="fieldLabel">GR Number</label>
                    <Select
                      className="grNumberSelect"
                      classNamePrefix="gr-number-select"
                      placeholder="Search GR Number"
                      value={
                        selectedGRNumber === "all"
                          ? { value: "all", label: "All GR Numbers" }
                          : selectedGRNumber
                            ? { value: selectedGRNumber, label: selectedGRNumber }
                            : null
                      }
                      onChange={(selected) =>
                        setSelectedGRNumber(selected ? selected.value : "")
                      }
                      options={[
                        { value: "all", label: "All GR Numbers" },
                        ...getAvailableGRNumbers().map((no) => ({
                          value: no,
                          label: no,
                        })),
                      ]}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                        }),
                        control: (base) => ({
                          ...base,
                          minHeight: "44px",
                          borderRadius: "10px",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>

                  <div className="fieldWrap deliveryDateField">
                    <label className="fieldLabel">Delivery Date</label>
                    <Select
                      className="deliveryDateReactSelect"
                      classNamePrefix="delivery-date-select"
                      placeholder="Search Delivery Date"
                      value={
                        selectedDeliveryTime === "all"
                          ? { value: "all", label: "All Delivery Dates" }
                          : selectedDeliveryTime
                            ? { value: selectedDeliveryTime, label: selectedDeliveryTime }
                            : null
                      }
                      onChange={(selected) =>
                        setSelectedDeliveryTime(selected ? selected.value : "")
                      }
                      options={[
                        { value: "all", label: "All Delivery Dates" },
                        ...getAvailableDeliveryDates().map((d) => ({
                          value: d,
                          label: d,
                        })),
                      ]}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                        }),
                        control: (base) => ({
                          ...base,
                          minHeight: "44px",
                          borderRadius: "10px",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                          borderRadius: "10px",
                          overflow: "hidden",
                        }),
                      }}
                    />
                  </div>

                  <div className="filterBtnWrap clearFilterWrap">
                    <button
                      className="btn btnOutline btnFull clearFilterBtn"
                      type="button"
                      onClick={() => {
                        setSelectedDealerCode("all")
                        setSelectedGRNumber("all")
                        setSelectedDeliveryTime("all")
                      }}
                    >
                      Clear GR Filters
                    </button>
                  </div>
                </div>
              </div>

              <div className="tableWrap">
                {grLevelLoading ? (
                  <div className="ddLoadingWrap">
                    <div className="ddLoadingText">Loading GR-level data...</div>
                  </div>
                ) :
                  <div
                    className="ag-theme-balham"
                    style={{ width: "100%", height: 420 }}
                  >
                    <AgGridReact
                      rowData={getGrRowsForUi()}
                      columnDefs={getHubGrLevelColumnDefs()}
                      defaultColDef={{
                        sortable: true,
                        filter: "agSetColumnFilter",
                        floatingFilter: false,
                        resizable: true,
                        minWidth: 120,
                        menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
                        filterParams: {
                          buttons: ["reset", "apply"],
                          closeOnApply: true,
                          suppressMiniFilter: false,
                          refreshValuesOnOpen: true,
                        },
                      }}
                      animateRows={true}
                      headerHeight={45}
                      rowHeight={45}
                      suppressCellFocus={true}
                      pagination={true}
                      paginationPageSize={20}
                      enableRangeSelection={true}
                      paginationPageSizeSelector={[20, 50, 100, 200]}
                      loading={grLevelLoading}
                      sideBar={{
                        toolPanels: [
                          {
                            id: "columns",
                            labelDefault: "Columns",
                            labelKey: "columns",
                            iconKey: "columns",
                            toolPanel: "agColumnsToolPanel",
                            toolPanelParams: {
                              suppressPivotMode: true,
                              suppressRowGroups: true,
                              suppressValues: true,
                            },
                          },
                          {
                            id: "filters",
                            labelDefault: "Filters",
                            labelKey: "filters",
                            iconKey: "filter",
                            toolPanel: "agFiltersToolPanel",
                          },
                        ],
                      }}
                      statusBar={{
                        statusPanels: [
                          { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                          { statusPanel: "agTotalRowCountComponent", align: "center" },
                          { statusPanel: "agFilteredRowCountComponent" },
                          { statusPanel: "agSelectedRowCountComponent" },
                          { statusPanel: "agAggregationComponent" },
                        ],
                      }}
                    />
                  </div>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* GURGAON TAB */}
      {activeSubTab === "gurgaon" && (
        <div className="ddSection">
          {currentView !== "hub" ? (
            <DashboardHeaderBanner>
              <div className="ddHeaderRow">
                <div className="ddHeaderLeft">
                  <h2 className="ddSubtitle ddSubtitleOrange">Gurgaon Warehouse (90hr/120hr)</h2>
                </div>
                <div className="ddHeaderRight">
                  <div className="ddRefreshText">Last refreshed: {formatRefreshTime(lastRefreshTime)}</div>
                  <button className="btn btnOutline" onClick={handleBack} type="button">
                    <span className="btnIcon">←</span>
                    Back
                  </button>
                </div>
              </div>
            </DashboardHeaderBanner>
          ) : (
            <div>
              <DashboardHeaderBanner>
                <div className="ddHeaderRow ddHeaderCompact">
                  <h2 className="ddSubtitle ddSubtitleOrange">Gurgaon Warehouse (90hr/120hr)</h2>
                  <div className="ddRefreshText">Last refreshed: {formatRefreshTime(lastRefreshTime)}</div>
                </div>
              </DashboardHeaderBanner>
              <div className="hmCardBody hmCardBodyCompact" style={{ paddingLeft: "0px" }}>
                <div className="hmFilterGridStyle">
                  <div className="hmField">
                    <label className="hmLabel" htmlFor="from-date">
                      From Date
                    </label>
                    <input
                      id="from-date"
                      className="hmInput"
                      type="date"
                      value={fromDate}
                      onChange={function (e) {
                        setFromDate(e.target.value)
                      }}
                    />
                  </div>

                  <div className="hmField">
                    <label className="hmLabel" htmlFor="to-date">
                      To Date
                    </label>
                    <input
                      id="to-date"
                      className="hmInput"
                      type="date"
                      value={toDate}
                      onChange={function (e) {
                        setToDate(e.target.value)
                      }}
                    />
                  </div>
                  <div className="hmField">
                    <label className="hmLabel">Warehouse</label>
                    <Select
                      isMulti
                      className="warehouse-select"
                      classNamePrefix="warehouse-select"
                      options={warehousesList}
                      value={selectedWarehouses}
                      onChange={handleWarehouseChange}
                    />


                  </div>



                  <div className="hmField hmFieldBtn">
                    <button className="submit-btn" type="button" onClick={submitDateFilters}>
                      Submit
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {loading &&
            <div className="ddLoadingWrap">
              <div className="ddLoadingText">Loading dashboard...</div>
            </div>}

          {(currentView === "hub" && !loading) && (
            <div className="ddBlock">
              {/* KPI */}
              <div className="kpiGridGurgaon">
                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon orangeSoft">
                      <svg className="kpiSvg orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-2 0H3m2-16h14" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Gurgaon Hubs</div>
                      <div className="kpiValue">{gurgaonCountersData.gurgaonHubs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon blueSoft">
                      <svg className="kpiSvg blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Total GRs</div>
                      <div className="kpiValue">{gurgaonCountersData.totalGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon greenSoft">
                      <svg className="kpiSvg green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Total Delivered</div>
                      <div className="kpiValue">{gurgaonCountersData.deliveredGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon orangeSoft">
                      <svg className="kpiSvg orange" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">In Transit GRs</div>
                      <div className="kpiValue">{gurgaonCountersData.intransitGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon redSoft">
                      <svg className="kpiSvg red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Invalid GRs</div>
                      <div className="kpiValue">{gurgaonCountersData.invalidGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon yellowSoft">
                      <svg className="kpiSvg yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Hub Not Mapped GRs</div>
                      <div className="kpiValue">{gurgaonCountersData.unmappedHubGRs}</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon yellowSoft">
                      <svg className="kpiSvg yellow" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">{"<"}120h GRs</div>
                      <div className="kpiValue">{gurgaonCountersData.within120Hrs} ({gurgaonCountersData.within120HrsPct}%)</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon redSoft">
                      <svg className="kpiSvg red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">&gt;120hr GRs</div>
                      <div className="kpiValue">{gurgaonCountersData.gt120Hrs} ({gurgaonCountersData.gt120HrsPct}%)</div>
                    </div>
                  </div>
                </div>

                <div className="card kpiCard">
                  <div className="kpiContent">
                    <div className="kpiIcon greenSoft">
                      ₹
                    </div>
                    <div className="kpiText">
                      <div className="kpiLabel">Savings (₹)</div>
                      <div className="kpiValue">{`${gurgaonCountersData.savings}M`}</div>
                    </div>
                  </div>
                </div>
              </div>

              {showGurgaonHubOverview && (
                <div className="card">
                  <div className="cardHeader">
                    <div className="cardHeaderRow">
                      <div className="cardTitle orangeText">Hub Performance Overview</div>
                      <PerformanceLegend config={perfConfig} />
                    </div>
                  </div>

                  <div className="cardBody">
                    <div className="tableWrap">
                      <div
                        className="ag-theme-balham ddAgGrid ddAgGridOrange"
                        style={{ width: "100%", height: 420 }}
                      >
                        <AgGridReact
                          rowData={gurgaonHubSummary}
                          columnDefs={getGurgaonHubColumnDefs()}
                          defaultColDef={{
                            sortable: true,
                            filter: "agSetColumnFilter",
                            floatingFilter: false,
                            resizable: true,
                            minWidth: 90,
                            menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
                            filterParams: {
                              buttons: ["reset", "apply"],
                              closeOnApply: true,
                              suppressMiniFilter: false,
                              refreshValuesOnOpen: true,
                            },
                          }}
                          animateRows={true}
                          headerHeight={45}
                          rowHeight={70}
                          suppressCellFocus={true}
                          pagination={true}
                          paginationPageSize={20}
                          enableRangeSelection={true}
                          paginationPageSizeSelector={[20, 50, 100, 200]}
                          sideBar={{
                            toolPanels: [
                              {
                                id: "columns",
                                labelDefault: "Columns",
                                labelKey: "columns",
                                iconKey: "columns",
                                toolPanel: "agColumnsToolPanel",
                                toolPanelParams: {
                                  suppressPivotMode: true,
                                  suppressRowGroups: true,
                                  suppressValues: true,
                                },
                              },
                              {
                                id: "filters",
                                labelDefault: "Filters",
                                labelKey: "filters",
                                iconKey: "filter",
                                toolPanel: "agFiltersToolPanel",
                              },
                            ],
                          }}
                          statusBar={{
                            statusPanels: [
                              { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                              { statusPanel: "agTotalRowCountComponent", align: "center" },
                              { statusPanel: "agFilteredRowCountComponent" },
                              { statusPanel: "agSelectedRowCountComponent" },
                              { statusPanel: "agAggregationComponent" },
                            ],
                          }}
                        />
                      </div>
                    </div>

                  </div>
                </div>
              )}

              {/* Gurgaon Performance Comparison (UNCHANGED) */}
              <div className="card">
                <div className="cardHeader">
                  <div className="cardHeaderRow">
                    <div className="cardTitle">Performance Comparison</div>
                    <div className="btnGroup">
                      <ButtonSimple
                        variant={gurgaonChartView === "gr" ? "primary" : "outline"}
                        onClick={function () {
                          setGurgaonChartView("gr")
                        }}
                      >
                        GR
                      </ButtonSimple>
                      <ButtonSimple
                        variant={gurgaonChartView === "transit" ? "primary" : "outline"}
                        onClick={function () {
                          setGurgaonChartView("transit")
                        }}
                      >
                        %
                      </ButtonSimple>
                    </div>
                  </div>

                  <div className="legendRow below">
                    <span className="legendItem">
                      <span className="legendBox greenFill" /> Delivered
                    </span>
                    <span className="legendItem">
                      <span className="legendBox blueFill" /> Within 90 Hours
                    </span>
                    <span className="legendItem">
                      <span className="legendBox yellowFill" /> 90 to 120 Hours
                    </span>
                    <span className="legendItem">
                      <span className="legendBox redFill" /> &gt; 120 Hours
                    </span>
                  </div>
                </div>

                <div className="cardBody">
                  <div className="perfStack">
                    {(gurgaonPerfComparison || [])
                      .slice()
                      .sort(function (a, b) {
                        var aTotal = a.delivered_grs || 1
                        var bTotal = b.delivered_grs || 1

                        var aPerf = ((aTotal - (a.above_120h || 0)) / aTotal) * 100
                        var bPerf = ((bTotal - (b.above_120h || 0)) / bTotal) * 100

                        return bPerf - aPerf
                      })
                      .slice(0, 5)
                      .map(function (hub, index) {
                        var total = hub.delivered_grs || 1

                        var within48 = hub.delivered_within_48h || 0
                        var band48to90 = hub.delivered_in_between_48h_and_90h || 0
                        var band90to120 = hub.delivered_in_between_90h_and_120h || 0
                        var over120 = hub.above_120h || 0

                        var deliveredTopLabel = hub.delivered_grs || 0

                        var pctBlue = (within48 / total) * 100
                        var pctYellow = (band48to90 / total) * 100
                        var pctOrange = (band90to120 / total) * 100
                        var pctRed = (over120 / total) * 100

                        var wBlue = gurgaonChartView === "gr" ? pctBlue : pctBlue
                        var wYellow = gurgaonChartView === "gr" ? pctYellow : pctYellow
                        var wOrange = gurgaonChartView === "gr" ? pctOrange : pctOrange
                        var wRed = gurgaonChartView === "gr" ? pctRed : pctRed

                        function segLabel(value, pct) {
                          if (gurgaonChartView === "gr") {
                            if (pct < 6) return ""
                            return String(value) + "(" + pct.toFixed(0) + "%)"
                          } else {
                            if (pct < 6) return ""
                            return pct.toFixed(0) + "%"
                          }
                        }

                        return (
                          <div className="perfBlock" key={`${hub.warehouse}-${index}`}>
                            <div className="perfHubTitle">{hub.warehouse}</div>

                            <div className="perfRow">
                              <div className="perfTopBar">
                                <div className="perfTopFill greenFill">
                                  <span className="barText">{deliveredTopLabel}</span>
                                </div>
                              </div>

                              <div className="perfBottomBar">
                                <div className="seg blueFill" style={{ width: wBlue + "%" }}>
                                  <span className="barText">{segLabel(within48, pctBlue)}</span>
                                </div>

                                <div className="seg yellowFill" style={{ width: wYellow + "%" }}>
                                  <span className="barText">{segLabel(band48to90, pctYellow)}</span>
                                </div>

                                <div className="seg orangeFill" style={{ width: wOrange + "%" }}>
                                  <span className="barText">{segLabel(band90to120, pctOrange)}</span>
                                </div>

                                <div className="seg redFill" style={{ width: wRed + "%" }}>
                                  <span className="barText">{segLabel(over120, pctRed)}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                  </div>
                </div>
              </div>

              {/* Transit Time Trend Gurgaon */}
              <div className="card">
                <div className="cardHeader">
                  <div className="cardHeaderRow">
                    <div className="cardTitle">Transit Time Trend</div>

                    <div className="filterRow">
                      <SelectSimple
                        value={gurgaonWarehouse}
                        onChange={function (e) {
                          setGurgaonWarehouse(e.target.value)
                        }}
                      >
                        <option value="all">All Warehouses</option>
                        {Array.from(new Set(gurgaonHubSummary.map((h) => h.warehouse).filter((h) => h && String(h).trim() !== ""))).map((w) => (
                          <option key={w} value={w}>
                            {w}
                          </option>
                        ))}
                      </SelectSimple>

                      <SelectSimple
                        value={gurgoanTrendHub}
                        onChange={function (e) {
                          setGurgoanTrendHub(e.target.value)
                        }}
                      >
                        <option value="all">All Hubs</option>
                        {gurgaonHubSummary.map(function (hub, index) {
                          return (
                            <option key={`${hub.hub_location}-${index}`} value={hub.hub_location}>
                              {hub.hub_location}
                            </option>
                          )
                        })}
                      </SelectSimple>

                      <SelectSimple
                        value={timeRangeGurgaon}
                        onChange={function (e) {
                          setTimeRangeGurgaon(e.target.value)
                        }}
                      >
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </SelectSimple>
                    </div>
                  </div>
                </div>

                <div className="cardBody">
                  <div className="chartBox">
                    {trendLoading ? (
                      <div className="trendLoaderWrap">
                        <div className="trendLoader">Loading trend...</div>
                      </div>
                    ) : (
                      <TransitTimeTrendChart data={gurgoanTrendData} warehouseType="gurgaon" timePeriod={timeRangeGurgaon} />

                    )}
                  </div>

                  <MiniCardsFromTrend data={gurgoanTrendData} labelShown="Total Hubs" />
                </div>
              </div>
            </div>
          )}

          {/* Gurgaon City view */}
          {currentView === "city" && selectedHub && (
            <div className="card ddCardPad">
              <div className="ddCardHeaderRow">
                <h3 className="ddCardTitle">Hub-Wise (City-Level) Details - {selectedHub}</h3>
              </div>

              <div className="tableWrap">
                {gurgaonCityLevelLoading ? (
                  <div className="ddLoaderWrap">
                    <div className="ddLoadingText">Loading city-level data...</div>
                  </div>
                ) : (
                  <div
                    className="ag-theme-balham"
                    style={{ width: "100%", height: 420 }}
                  >
                    <AgGridReact
                      rowData={getGurgaonCityDetailsFromApiRows()}
                      columnDefs={getGurgaonCityLevelColumnDefs()}
                      defaultColDef={{
                        sortable: true,
                        filter: "agSetColumnFilter",
                        floatingFilter: false,
                        resizable: true,
                        minWidth: 120,
                        menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
                        filterParams: {
                          buttons: ["reset", "apply"],
                          closeOnApply: true,
                          suppressMiniFilter: false,
                          refreshValuesOnOpen: true,
                        },
                      }}
                      animateRows={true}
                      headerHeight={45}
                      rowHeight={45}
                      suppressCellFocus={true}
                      pagination={true}
                      paginationPageSize={20}
                      enableRangeSelection={true}
                      paginationPageSizeSelector={[20, 50, 100, 200]}
                      sideBar={{
                        toolPanels: [
                          {
                            id: "columns",
                            labelDefault: "Columns",
                            labelKey: "columns",
                            iconKey: "columns",
                            toolPanel: "agColumnsToolPanel",
                            toolPanelParams: {
                              suppressPivotMode: true,
                              suppressRowGroups: true,
                              suppressValues: true,
                            },
                          },
                          {
                            id: "filters",
                            labelDefault: "Filters",
                            labelKey: "filters",
                            iconKey: "filter",
                            toolPanel: "agFiltersToolPanel",
                          },
                        ],
                      }}
                      statusBar={{
                        statusPanels: [
                          { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                          { statusPanel: "agTotalRowCountComponent", align: "center" },
                          { statusPanel: "agFilteredRowCountComponent" },
                          { statusPanel: "agSelectedRowCountComponent" },
                          { statusPanel: "agAggregationComponent" },
                        ],
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Gurgaon GR view reuses same GR view above (currentView === "gr") */}
          {currentView === "gr" && selectedHub && selectedCity && (
            <div className="card ddCardPad">
              <div className="ddCardHeaderRow">
                <h3 className="ddCardTitle">
                  GR-Level Details - {selectedHub} / {selectedCity} / {selectedGRType}
                </h3>
              </div>

              <div className="filterPanel">
                <div className="filterTop">
                  <div className="filterIcon">⏷</div>
                  <div className="filterTitle">GR Level Filters</div>
                </div>

                <div className="filterGrid">
                  <div className="fieldWrap dealerCodeField">
                    <label className="fieldLabel">Dealer Code</label>
                    <Select
                      className="dealerCodeSelect"
                      classNamePrefix="dealer-code-select"
                      placeholder="Search Dealer Code"
                      value={
                        selectedDealerCode === "all"
                          ? { value: "all", label: "All Dealer Codes" }
                          : selectedDealerCode
                            ? { value: selectedDealerCode, label: selectedDealerCode }
                            : null
                      }
                      onChange={(selected) =>
                        setSelectedDealerCode(selected ? selected.value : "")
                      }
                      options={[
                        { value: "all", label: "All Dealer Codes" },
                        ...getAvailableDealerCodes().map((code) => ({
                          value: code,
                          label: code,
                        })),
                      ]}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                        }),
                        control: (base) => ({
                          ...base,
                          minHeight: "44px",
                          width: "100%",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>

                  <div className="fieldWrap grNumberField">
                    <label className="fieldLabel">GR Number</label>
                    <Select
                      className="grNumberSelect"
                      classNamePrefix="gr-number-select"
                      placeholder="Search GR Number"
                      value={
                        selectedGRNumber === "all"
                          ? { value: "all", label: "All GR Numbers" }
                          : selectedGRNumber
                            ? { value: selectedGRNumber, label: selectedGRNumber }
                            : null
                      }
                      onChange={(selected) =>
                        setSelectedGRNumber(selected ? selected.value : "")
                      }
                      options={[
                        { value: "all", label: "All GR Numbers" },
                        ...getAvailableGRNumbers().map((no) => ({
                          value: no,
                          label: no,
                        })),
                      ]}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                        }),
                        control: (base) => ({
                          ...base,
                          minHeight: "44px",
                          borderRadius: "10px",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                      }}
                    />
                  </div>

                  <div className="fieldWrap deliveryDateField">
                    <label className="fieldLabel">Delivery Date</label>
                    <Select
                      className="deliveryDateReactSelect"
                      classNamePrefix="delivery-date-select"
                      placeholder="Search Delivery Date"
                      value={
                        selectedDeliveryTime === "all"
                          ? { value: "all", label: "All Delivery Dates" }
                          : selectedDeliveryTime
                            ? { value: selectedDeliveryTime, label: selectedDeliveryTime }
                            : null
                      }
                      onChange={(selected) =>
                        setSelectedDeliveryTime(selected ? selected.value : "")
                      }
                      options={[
                        { value: "all", label: "All Delivery Dates" },
                        ...getAvailableDeliveryDates().map((d) => ({
                          value: d,
                          label: d,
                        })),
                      ]}
                      isSearchable
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                        }),
                        control: (base) => ({
                          ...base,
                          minHeight: "44px",
                          borderRadius: "10px",
                        }),
                        menuPortal: (base) => ({
                          ...base,
                          zIndex: 9999,
                        }),
                        menu: (base) => ({
                          ...base,
                          zIndex: 9999,
                          borderRadius: "10px",
                          overflow: "hidden",
                        }),
                      }}
                    />
                  </div>

                  <div className="filterBtnWrap clearFilterWrap">
                    <button
                      className="btn btnOutline btnFull clearFilterBtn"
                      type="button"
                      onClick={() => {
                        setSelectedDealerCode("all")
                        setSelectedGRNumber("all")
                        setSelectedDeliveryTime("all")
                      }}
                    >
                      Clear GR Filters
                    </button>
                  </div>
                </div>
              </div>

              <div className="tableWrap">
                {grLevelLoading ? (
                  <div className="ddLoadingWrap">
                    <div className="ddLoadingText">Loading GR-level data...</div>
                  </div>
                ) :
                  <div className="ag-theme-balham" style={{ width: "100%", height: 420 }}>
                    <AgGridReact
                      rowData={getGrRowsForUi()}
                      columnDefs={getGrLevelColumnDefs()}
                      defaultColDef={{
                        sortable: true,
                        filter: "agSetColumnFilter",
                        floatingFilter: false,
                        resizable: true,
                        minWidth: 120,
                        menuTabs: ["filterMenuTab", "generalMenuTab", "columnsMenuTab"],
                        filterParams: {
                          buttons: ["reset", "apply"],
                          closeOnApply: true,
                          suppressMiniFilter: false,
                          refreshValuesOnOpen: true,
                        },
                      }}
                      animateRows={true}
                      headerHeight={45}
                      rowHeight={45}
                      suppressCellFocus={true}
                      pagination={true}
                      paginationPageSize={20}
                      enableRangeSelection={true}
                      paginationPageSizeSelector={[20, 50, 100, 200]}
                      loading={grLevelLoading}
                      sideBar={{
                        toolPanels: [
                          {
                            id: "columns",
                            labelDefault: "Columns",
                            labelKey: "columns",
                            iconKey: "columns",
                            toolPanel: "agColumnsToolPanel",
                            toolPanelParams: {
                              suppressPivotMode: true,
                              suppressRowGroups: true,
                              suppressValues: true,
                            },
                          },
                          {
                            id: "filters",
                            labelDefault: "Filters",
                            labelKey: "filters",
                            iconKey: "filter",
                            toolPanel: "agFiltersToolPanel",
                          },
                        ],
                      }}
                      statusBar={{
                        statusPanels: [
                          { statusPanel: "agTotalAndFilteredRowCountComponent", align: "left" },
                          { statusPanel: "agTotalRowCountComponent", align: "center" },
                          { statusPanel: "agFilteredRowCountComponent" },
                          { statusPanel: "agSelectedRowCountComponent" },
                          { statusPanel: "agAggregationComponent" },
                        ],
                      }}
                    />
                  </div>}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function computeSummaryFromTrend(trendArr, topN, activeSubTab) {
  var agg = {}
  for (var i = 0; i < trendArr.length; i++) {
    var r = trendArr[i]
    var hub = ""
    if (activeSubTab === "all") {
      hub = r && r.consigner_code ? r.consigner_code : ""
    } else {
      hub = r && r.hub_location ? r.hub_location : ""
    }
    if (!hub) continue
    var avg = typeof r.avg_duration === "number" ? r.avg_duration : parseFloat(r.avg_duration || 0)
    var cnt = typeof r.count === "number" ? r.count : parseFloat(r.count || 0)

    if (!agg[hub]) agg[hub] = { sumWeighted: 0, sumCount: 0, totalCount: 0 }
    agg[hub].sumWeighted += avg * (cnt > 0 ? cnt : 1)
    agg[hub].sumCount += cnt > 0 ? cnt : 1
    agg[hub].totalCount += cnt > 0 ? cnt : 1
  }

  var hubs = Object.keys(agg)
  if (hubs.length === 0) {
    return { shownCount: 0, bestHub: "", bestAvg: 0, poorHub: "", poorAvg: 0 }
  }

  hubs.sort(function (a, b) {
    return agg[b].totalCount - agg[a].totalCount
  })
  var shown = hubs.slice(0, topN)

  var bestHub = ""
  var bestAvg = Infinity
  var poorHub = ""
  var poorAvg = -Infinity

  for (var j = 0; j < shown.length; j++) {
    var h = shown[j]
    var denom = agg[h].sumCount || 1
    var overallAvg = agg[h].sumWeighted / denom

    if (overallAvg < bestAvg) {
      bestAvg = overallAvg
      bestHub = h
    }
    if (overallAvg > poorAvg) {
      poorAvg = overallAvg
      poorHub = h
    }
  }

  return {
    shownCount: shown.length,
    bestHub: bestHub,
    bestAvg: bestAvg === Infinity ? 0 : bestAvg,
    poorHub: poorHub,
    poorAvg: poorAvg === -Infinity ? 0 : poorAvg,
  }
}
function buildWarehouseHubMap(apiRows) {
  var map = {};
  for (var i = 0; i < apiRows.length; i++) {
    var w = apiRows[i] && apiRows[i].warehouse ? apiRows[i].warehouse : "";
    var hubs = apiRows[i] && Array.isArray(apiRows[i].hub) ? apiRows[i].hub : [];
    if (w) map[w] = hubs.slice(0);
  }
  return map;
}

function buildWarehouseOptionsFromMap(map, activeSubTab) {
  var warehouseNameMap = {
    MSILG: "GURGAON",
    MSILK: "KHARKHODA"
  };

  var keys = Object.keys(map).sort();
  var opts = [{ label: "All", value: "all" }];

  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];

    opts.push({
      label: activeSubTab === "gurgaon" ? (warehouseNameMap[key] || key) : key,
      value: key
    });
  }

  return opts;
}
const formatDateTime = (value) => {
  if (!value) return "";

  const str = String(value).trim();

  // Handles: "2025-12-03 20:30:00"
  const match = str.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::\d{2})?$/
  );

  if (match) {
    const [, year, month, day, hour, minute] = match;
    return `${day}-${month}-${year} ${hour}:${minute}`;
  }

  const date = new Date(str);
  if (isNaN(date.getTime())) return str;

  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};
function formatInputDate(value) {
  if (!value) return "";
  var d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";

  var dd = String(d.getDate()).padStart(2, "0");
  var mm = String(d.getMonth() + 1).padStart(2, "0");
  var yyyy = d.getFullYear();

  return yyyy + "-" + mm + "-" + dd;
}